/*
  JapanSolved™ Intake Intelligence Controller v7.7.2 Original NIF Backend Reconciliation Pass v1.4
  Theme Sync + Strict Email Validation + Final Gate Dedup + Button Wrap Repair + Native Submit Bridge
  - Unified mobile/desktop option controls
  - Exact missing-field validation with scroll guidance
  - Review-before-submit case-file screen
  - Elite internal intake email formatter
  - Client-facing submitted-details receipt and success state
  - Public technical routing wording removed
  v7.6 Standalone Shopify-Native Email Cleanup + Receipt Baseline
  - Progress-aware scroll smoothing with reduced-motion respect
  - Step rail indicators synced to the active paginated step
  - IntersectionObserver attention steering for active fields
  - Auto-expanding textarea behavior
  - Submit cooldown / double-click submission lock
*/
(function(){
  'use strict';

  const NS = 'JSIntakeIntelligence';
  const STORAGE_PREFIX = 'jsii:original-nif-v1-3:draft:';
  const RECEIPT_PREFIX = 'jsii:original-nif-v1-3:receipt:';
  const ENGINE_VERSION = 'old-nif-v1-9-24-private-sourcing-alias-connection-lock';
  const SUBMIT_LOCK_MS = 4000;
  const FOLLOWUP_URL = 'https://japansolved.com/pages/follow-up-on-existing-request';
  const URGENT_VALUES = ['Within 72 hours','Within 24 hours','Already overdue / already committed','Travel / event / pickup / seller deadline exists','Auction / seller / pickup deadline','Urgent / high-stakes timing'];

  const UNIVERSAL_SUPPORT_STEP_ID = 'supporting_references_uploads';
  const UNIVERSAL_SUPPORT_FIELDS = [
    {
      id:'supporting_upload_links',
      aliases:['supporting_reference_links','supporting_links','reference_links','upload_folder','file_share_folder'],
      label:'Reference links or upload folder',
      type:'textarea',
      ui:'upload_reference_card',
      required:false,
      placeholder:'Paste Dropbox, Google Drive, iCloud, OneDrive, WeTransfer, product pages, venue pages, screenshot folders, PDF folders, image links, or other reference links.',
      helper:'Optional, but useful. Paste public links or private file-share folders here. This secure contact route does not directly attach binary files, so use a share link rather than dragging files into the form.'
    },
    {
      id:'supporting_file_notes',
      aliases:['file_photo_pdf_notes','supporting_reference_notes','upload_notes','document_notes'],
      label:'Notes about the files or references',
      type:'textarea',
      required:false,
      placeholder:'Tell us what the links contain, what matters, which files or screenshots are most important, or what you can send after JapanSolved™ replies.',
      helper:'Do not send passports, private medical records, hotel-room numbers, private addresses, confidential contracts, or unauthorized third-party personal data unless JapanSolved™ specifically requests them through a controlled path.'
    }
  ];

  function qs(root, sel){ return (root || document).querySelector(sel); }
  function qsa(root, sel){ return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function esc(str){ return String(str == null ? '' : str).replace(/[&<>"']/g, s => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[s])); }
  function slug(str){ return String(str || '').toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,''); }
  function create(tag, cls, html){ const el = document.createElement(tag); if(cls) el.className = cls; if(html != null) el.innerHTML = html; return el; }
  function stripHtml(str){ return String(str || '').replace(/<[^>]*>/g,' ').replace(/\s+/g,' ').trim(); }
  function nowJST(){
    try { return new Intl.DateTimeFormat('en-CA',{timeZone:'Asia/Tokyo',year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit',second:'2-digit'}).format(new Date()); }
    catch(e){ return new Date().toISOString(); }
  }
  function urlParam(name){ try { return new URLSearchParams(window.location.search).get(name); } catch(e){ return null; } }

  function getRegistry(){
    if(window.JSIntakeRegistry && Array.isArray(window.JSIntakeRegistry.records)) return window.JSIntakeRegistry;
    const script = document.getElementById('js-intake-registry-json');
    if(script){ try { return JSON.parse(script.textContent); } catch(e){} }
    return {meta:{},records:[]};
  }

  function findRecord(handle){
    const registry = getRegistry();
    const candidate = String(handle || urlParam('intake') || '').trim();
    const exact = registry.records.find(r => r.page_handle === candidate) ||
      registry.records.find(r => Array.isArray(r.alternate_page_handles) && r.alternate_page_handles.indexOf(candidate) >= 0) ||
      registry.records.find(r => r.page_sequence === candidate) ||
      registry.records.find(r => r.record_id === candidate);
    if(exact) return normalizeRecordForEngine(exact);
    const fallback = registry.records.find(r => r.page_handle === 'generic-fallback-intake') || registry.records[0];
    if(fallback) fallback._fallback_reason = candidate ? ('No exact intake record found for '+candidate) : 'No intake handle supplied';
    return normalizeRecordForEngine(fallback);
  }

  function inferRouteLabel(route, index){
    const r = String(route || '').toLowerCase();
    if(r.indexOf('medical-tourism-support-review') >= 0) return 'Purchase the support review';
    if(r.indexOf('medical-tourism-entourage-case-deposit') >= 0) return 'Secure the case deposit';
    if(r.indexOf('private-entourage-support-retainer') >= 0) return 'Open the retainer path';
    if(r.indexOf('support-review') >= 0 || r.indexOf('review') >= 0) return 'Purchase the support review';
    if(r.indexOf('case-deposit') >= 0 || r.indexOf('deposit') >= 0) return 'Secure the case deposit';
    if(r.indexOf('retainer') >= 0) return 'Open the retainer path';
    return index === 0 ? 'Purchase the support review' : (index === 1 ? 'Secure the case deposit' : 'Open the payment path');
  }

  function routeObjects(record){
    if(Array.isArray(record.product_route_objects)) return record.product_route_objects.map((obj, idx) => ({
      route: obj.route || obj.value || obj.url || '',
      value: obj.value || obj.route || obj.url || '',
      label: obj.label || inferRouteLabel(obj.route || obj.value || obj.url, idx),
      slug_or_note: obj.slug_or_note || String(obj.route || obj.value || obj.url || '').replace('/products/',''),
      status: obj.status || 'ready_path',
      render_as_link: obj.render_as_link !== false
    }));
    return (record.product_routes || []).map((route, idx) => {
      if(route && typeof route === 'object'){
        const value = route.value || route.route || route.url || route.slug_or_note || '';
        const unresolvedObj = String(value).indexOf('VERIFY_PRODUCT_LEDGER:') === 0;
        return {
          route: unresolvedObj ? '' : value,
          value: value,
          label: route.label || (unresolvedObj ? ('Verify product route: '+String(value).replace('VERIFY_PRODUCT_LEDGER:','')) : inferRouteLabel(value, idx)),
          slug_or_note: route.slug_or_note || (unresolvedObj ? String(value).replace('VERIFY_PRODUCT_LEDGER:','') : String(value).replace('/products/','')),
          status: route.status || (unresolvedObj ? 'verify_before_hardwire' : 'ready_path'),
          render_as_link: route.render_as_link !== false && !!value && !unresolvedObj
        };
      }
      const unresolved = String(route).indexOf('VERIFY_PRODUCT_LEDGER:') === 0;
      return { route: unresolved ? '' : route, value: route, label: unresolved ? ('Verify product route: '+String(route).replace('VERIFY_PRODUCT_LEDGER:','')) : inferRouteLabel(route, idx), slug_or_note: unresolved ? String(route).replace('VERIFY_PRODUCT_LEDGER:','') : String(route).replace('/products/',''), status: unresolved ? 'verify_before_hardwire':'ready_path', render_as_link: !unresolved };
    });
  }

  function productOptions(record){
    const opts = routeObjects(record).map(obj => ({
      label: obj.status === 'verify_before_hardwire' ? 'Payment route pending confirmation: '+obj.slug_or_note : cleanRouteLabel(normalizeVisiblePaymentCopy(obj.label || inferRouteLabel(obj.route, 0))),
      value: obj.status === 'verify_before_hardwire' ? 'VERIFY_PRODUCT_LEDGER:'+obj.slug_or_note : (obj.route || obj.value || obj.slug_or_note)
    }));
    if(!opts.some(o => o.value === 'approved_private_payment_path')){
      opts.push({label:'Approved private payment path (invoice, access deposit, retainer, or custom quote)', value:'approved_private_payment_path'});
    }
    opts.push({label:'I am not sure', value:'not_sure'});
    return opts;
  }
  function normalizeOptions(field, record){
    if(field.options_from === 'product_routes') return productOptions(record);
    return (field.options || []).map(opt => typeof opt === 'string' ? {label: opt, value: opt} : opt);
  }

  function cleanRouteLabel(label){
    return String(label || '')
      .replace(/\s+[—-]\s+\/products\/[^\s]+/g,'')
      .replace(/\s+\/products\/[^\s]+/g,'')
      .replace(/\s+/g,' ')
      .trim();
  }

  function readDraft(record){
    try { return JSON.parse(localStorage.getItem(STORAGE_PREFIX+record.page_handle) || '{}'); } catch(e){ return {}; }
  }
  function saveDraft(record,state){ try { localStorage.setItem(STORAGE_PREFIX+record.page_handle, JSON.stringify({step:state.step, answers:state.answers, saved_at:new Date().toISOString()})); } catch(e){} }
  function clearDraft(record){ try { localStorage.removeItem(STORAGE_PREFIX+record.page_handle); } catch(e){} }
  function saveReceipt(record,data){ try { sessionStorage.setItem(RECEIPT_PREFIX+record.page_handle, JSON.stringify(data)); } catch(e){} }
  function readReceipt(record){ try { return JSON.parse(sessionStorage.getItem(RECEIPT_PREFIX+record.page_handle) || 'null'); } catch(e){ return null; } }
  function clearReceipt(record){ try { sessionStorage.removeItem(RECEIPT_PREFIX+record.page_handle); } catch(e){} }

  function prefersReducedMotion(){
    try { return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
    catch(e){ return false; }
  }
  function scrollBehavior(behavior){
    if(prefersReducedMotion()) return 'auto';
    return behavior || 'smooth';
  }
  function getHeaderOffset(){
    const selectors = ['sticky-header','header.site-header','.site-header','.header-wrapper','header[role="banner"]'];
    let h = 96;
    selectors.some(sel => {
      const el = qs(document, sel);
      if(!el) return false;
      const style = window.getComputedStyle(el);
      const pos = style.position;
      const rect = el.getBoundingClientRect();
      if((pos === 'fixed' || pos === 'sticky') && rect.height){ h = Math.max(72, Math.min(160, rect.height + 18)); return true; }
      return false;
    });
    if(window.innerWidth <= 749) h = Math.max(h, 118);
    return h;
  }
  function markScrollState(){
    document.documentElement.classList.add('jsi-scroll-in-flight');
    clearTimeout(markScrollState._t);
    markScrollState._t = setTimeout(()=>document.documentElement.classList.remove('jsi-scroll-in-flight'), prefersReducedMotion() ? 80 : 520);
  }
  function scrollToTarget(el, behavior){
    if(!el) return;
    const y = el.getBoundingClientRect().top + window.pageYOffset - getHeaderOffset();
    markScrollState();
    window.scrollTo({top:Math.max(0,y), behavior: scrollBehavior(behavior)});
  }
  function scrollToStepTop(container, behavior){
    const target = qs(container,'.jsi-step') || qs(container,'.jsi-review') || qs(container,'.jsi-success') || qs(container,'.jsi-card') || container;
    window.requestAnimationFrame(()=>setTimeout(()=>scrollToTarget(target, behavior || 'smooth'), prefersReducedMotion() ? 0 : 55));
  }
  function blurActive(){ try { if(document.activeElement && document.activeElement.blur) document.activeElement.blur(); } catch(e){} }

  function cleanupConcierge(container){
    if(!container || !Array.isArray(container.__jsiConciergeCleanups)) return;
    container.__jsiConciergeCleanups.splice(0).forEach(fn=>{ try { fn(); } catch(e){} });
  }
  function addConciergeCleanup(container, fn){
    if(!container) return;
    if(!Array.isArray(container.__jsiConciergeCleanups)) container.__jsiConciergeCleanups = [];
    container.__jsiConciergeCleanups.push(fn);
  }
  function refreshFieldCompletion(scope){
    qsa(scope,'.jsi-field').forEach(field=>{
      const box = qs(field,'[data-answer-for]');
      let v = '';
      if(box && box.classList && box.classList.contains('jsi-options')){
        v = qsa(field,'input[type="checkbox"], input[type="radio"]').some(x=>x.checked) ? 'selected' : '';
      } else if(box && box.classList && box.classList.contains('jsi-single-check')){
        const c = qs(box,'input[type="checkbox"]');
        v = c && c.checked ? 'selected' : '';
      } else if(box && box.classList && box.classList.contains('jsi-select-wrap')) {
        const sel = qs(box,'select');
        v = sel ? sel.value : '';
      } else if(box) {
        v = box.value;
      }
      field.classList.toggle('is-complete', valuePresent(v));
      field.classList.toggle('is-empty', !valuePresent(v));
    });
  }
  function setActiveField(scope, field){
    if(!scope || !field) return;
    qsa(scope,'.jsi-field.is-active').forEach(el=>{ if(el !== field) el.classList.remove('is-active'); });
    field.classList.add('is-active');
  }
  function setupTextareaAutoGrow(container, scope){
    qsa(scope,'textarea').forEach(textarea=>{
      const grow = ()=>{
        textarea.style.height = 'auto';
        textarea.style.height = Math.max(textarea.scrollHeight + 2, 142) + 'px';
      };
      textarea.classList.add('jsi-auto-grow');
      grow();
      textarea.addEventListener('input', grow);
      addConciergeCleanup(container,()=>textarea.removeEventListener('input', grow));
    });
  }
  function setupAttentionEvents(container, scope){
    const onFocusIn = ev => {
      const field = ev.target && ev.target.closest ? ev.target.closest('.jsi-field') : null;
      if(field && scope.contains(field)) setActiveField(scope, field);
    };
    const onInput = () => refreshFieldCompletion(scope);
    scope.addEventListener('focusin', onFocusIn);
    scope.addEventListener('input', onInput);
    scope.addEventListener('change', onInput);
    refreshFieldCompletion(scope);
    addConciergeCleanup(container,()=>{
      scope.removeEventListener('focusin', onFocusIn);
      scope.removeEventListener('input', onInput);
      scope.removeEventListener('change', onInput);
    });
  }
  function setupFieldObserver(container, scope){
    const fields = qsa(scope,'.jsi-field');
    if(!fields.length) return;
    setActiveField(scope, fields[0]);
    if(!('IntersectionObserver' in window)) return;
    const visible = new Map();
    const observer = new IntersectionObserver(entries=>{
      entries.forEach(entry=>visible.set(entry.target, entry.intersectionRatio || 0));
      const best = fields.slice().sort((a,b)=>(visible.get(b)||0)-(visible.get(a)||0))[0];
      if(best && (visible.get(best)||0) > 0.18) setActiveField(scope,best);
    }, {root:null, threshold:[0,.18,.32,.5,.72], rootMargin:'-18% 0px -42% 0px'});
    fields.forEach(field=>observer.observe(field));
    addConciergeCleanup(container,()=>observer.disconnect());
  }
  function setupConciergeUX(container, scope){
    if(!container || !scope) return;
    cleanupConcierge(container);
    container.classList.add('jsi-concierge-ready');
    if(prefersReducedMotion()) container.classList.add('jsi-reduced-motion');
    setupTextareaAutoGrow(container, scope);
    setupAttentionEvents(container, scope);
    setupFieldObserver(container, scope);
  }
  function renderStepRail(record, state){
    const total = (record.steps || []).length;
    const rail = create('div','jsi-step-rail');
    rail.setAttribute('aria-label','Intake step progress');
    for(let i=0;i<total;i++){
      const dot = create('span','jsi-step-dot',String(i+1));
      dot.setAttribute('aria-hidden','true');
      if(i < state.step) dot.classList.add('is-complete');
      if(i === state.step) dot.classList.add('is-active');
      rail.appendChild(dot);
    }
    rail.appendChild(create('span','jsi-step-rail-label','Step '+(state.step+1)+' / '+total));
    return rail;
  }

  function renderField(field, record, state){
    const wrap = create('div','jsi-field');
    if(field.ui === 'upload_reference_card') wrap.classList.add('jsi-upload-reference-field');
    if(field.elite_confirmation || field.summary_label) wrap.classList.add('jsi-confirmation-field');
    wrap.dataset.fieldId = field.id;
    if(field.condition) wrap.dataset.condition = JSON.stringify(field.condition);
    if(field.show_if) wrap.dataset.showIf = JSON.stringify(field.show_if);
    const id = 'jsi_'+slug(record.page_handle)+'_'+field.id;
    const req = field.required === true ? '<span class="jsi-req">*</span>' : '';
    const labelText = field.summary_label ? field.summary_label : displayFieldLabel(record, field.id, field.label || field.id);
    const label = create('label','jsi-label', esc(labelText)+' '+req);
    label.setAttribute('for', id);
    wrap.appendChild(label);
    const helperText = displayFieldHelper(record, field);
    if(helperText) wrap.appendChild(create('p','jsi-helper',esc(helperText)));
    if(field.ui === 'upload_reference_card'){
      const art = create('div','jsi-upload-visual','<span class="jsi-upload-icon" aria-hidden="true"><svg viewBox="0 0 96 96" focusable="false"><path d="M30 67H23c-8 0-15-7-15-15s6-15 14-15c3-12 14-21 27-21 14 0 25 10 28 23h1c8 0 14 6 14 14s-6 14-14 14h-9" fill="none" stroke="currentColor" stroke-width="6" stroke-linecap="round" stroke-linejoin="round"/><path d="M48 72V35" fill="none" stroke="currentColor" stroke-width="6" stroke-linecap="round"/><path d="M34 49l14-14 14 14" fill="none" stroke="currentColor" stroke-width="6" stroke-linecap="round" stroke-linejoin="round"/></svg></span><strong>Paste a secure file-share link</strong><em>Dropbox, Drive, iCloud, OneDrive, WeTransfer, product folders, screenshots, PDFs, or reference pages.</em></div>');
      wrap.appendChild(art);
    }
    let input;
    const val = state.answers[field.id];
    const options = normalizeOptions(field, record);
    switch(field.type){
      case 'textarea':
      case 'url_list':
        input = create('textarea','jsi-input'); input.id = id; input.rows = 5; input.value = val || ''; input.placeholder = field.placeholder || ''; break;
      case 'select': {
        const sel = create('select','jsi-input'); sel.id = id;
        sel.appendChild(new Option('Select one…',''));
        options.forEach(o => sel.appendChild(new Option(cleanRouteLabel(o.label), o.value)));
        sel.value = optionCanonicalValue(options, val) || val || '';
        input = create('span','jsi-select-wrap');
        input.appendChild(sel);
        break;
      }
      case 'radio':
        input = create('div','jsi-options');
        options.forEach((o,i)=>{
          const row = create('label','jsi-option jsi-option-radio');
          const r = document.createElement('input'); r.type='radio'; r.name=field.id; r.value=o.value; r.checked = optionValueMatches(o, val); r.id = id+'_'+i;
          row.appendChild(r); row.appendChild(create('span','jsi-control','')); const txt = document.createElement('span'); txt.className='jsi-option-text'; txt.textContent = o.label; row.appendChild(txt); input.appendChild(row);
        }); break;
      case 'checkbox_group':
        input = create('div','jsi-options');
        const arr = Array.isArray(val) ? val : [];
        options.forEach((o,i)=>{
          const row = create('label','jsi-option jsi-option-checkbox');
          const c = document.createElement('input'); c.type='checkbox'; c.name=field.id; c.value=o.value; c.checked = arr.some(v => optionValueMatches(o, v)); c.id = id+'_'+i;
          row.appendChild(c); row.appendChild(create('span','jsi-control','')); const txt = document.createElement('span'); txt.className='jsi-option-text'; txt.textContent = o.label; row.appendChild(txt); input.appendChild(row);
        }); break;
      case 'checkbox':
        input = create('label','jsi-option jsi-single-check jsi-option-checkbox');
        const c = document.createElement('input'); c.type='checkbox'; c.name=field.id; c.value='yes'; c.checked = !!val; c.id = id;
        input.appendChild(c); input.appendChild(create('span','jsi-control','')); const txt = document.createElement('span'); txt.className='jsi-option-text'; txt.textContent = field.text || 'I understand.'; input.appendChild(txt); break;
      case 'email':
      case 'url':
      case 'date':
      case 'tel':
      case 'text':
      default:
        input = document.createElement('input'); input.className='jsi-input'; input.id=id; input.type = ['email','url','date','tel'].includes(field.type) ? field.type : 'text'; input.value = val || ''; input.placeholder = field.placeholder || ''; break;
    }
    input.dataset.answerFor = field.id;
    input.dataset.required = field.required === true ? 'true' : 'false';
    if(field.required_logic) input.dataset.requiredLogic = field.required_logic;
    if(field.require_all) input.dataset.requireAll = 'true';
    wrap.appendChild(input);
    const error = create('p','jsi-error',''); error.hidden = true; wrap.appendChild(error);
    return wrap;
  }

  function collectVisibleAnswers(root){
    const answers = {};
    qsa(root,'.jsi-field:not([hidden])').forEach(fieldWrap=>{
      const id = fieldWrap.dataset.fieldId;
      const box = qs(fieldWrap,'[data-answer-for]');
      if(box && box.classList && box.classList.contains('jsi-options')){
        const checks = qsa(fieldWrap,'input[type="checkbox"]');
        const radios = qsa(fieldWrap,'input[type="radio"]');
        if(checks.length) answers[id] = checks.filter(x=>x.checked).map(x=>x.value);
        else if(radios.length){ const r = radios.find(x=>x.checked); answers[id] = r ? r.value : ''; }
      } else if(box && box.classList && box.classList.contains('jsi-single-check')){
        const c = qs(box,'input[type="checkbox"]');
        answers[id] = !!(c && c.checked);
      } else {
        let el = null;
        if(box && box.classList && box.classList.contains('jsi-select-wrap')) el = qs(box,'select');
        else el = box || qs(fieldWrap,'input, textarea, select');
        if(!el) return;
        if(el.type === 'checkbox') answers[id] = el.checked;
        else answers[id] = el.value;
      }
    });
    return answers;
  }

  function valuePresent(v){ return Array.isArray(v) ? v.length > 0 : !!String(v || '').trim(); }
  function isValidEmail(value){
    const raw = String(value || '').trim();
    if(!raw || raw.length > 254) return false;
    if(/[\s<>;,]/.test(raw)) return false;
    const parts = raw.split('@');
    if(parts.length !== 2) return false;
    const local = parts[0];
    const domain = parts[1];
    if(!local || !domain || local.length > 64) return false;
    if(local.startsWith('.') || local.endsWith('.') || local.indexOf('..') >= 0) return false;
    if(domain.startsWith('-') || domain.endsWith('-') || domain.indexOf('..') >= 0) return false;
    if(domain.indexOf('.') < 0) return false;
    const labels = domain.split('.');
    if(labels.some(label => !label || label.length > 63 || label.startsWith('-') || label.endsWith('-'))) return false;
    const tld = labels[labels.length-1];
    if(!/^[A-Za-z]{2,24}$/.test(tld)) return false;
    return /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+$/.test(raw);
  }
  function isEmailField(record, id, el){
    const field = fieldMap(record)[id];
    return (field && field.type === 'email') || (el && String(el.type || '').toLowerCase() === 'email') || /(^|_)email($|_)/i.test(String(id || ''));
  }
  function isPaid(state){
    const v = String(state.answers.payment_status || '').toLowerCase();
    return v.indexOf('yes') >= 0 || v.indexOf('completed payment') >= 0 || v.indexOf('instructed') >= 0 || v.indexOf('already secured') >= 0;
  }
  function isUnsurePayment(state){ return String(state.answers.payment_status || '').toLowerCase().indexOf('not sure') >= 0; }
  function isUrgent(state){ return URGENT_VALUES.some(x => String(state.answers.urgency || '').toLowerCase().indexOf(x.toLowerCase().slice(0,8)) >= 0); }

  function cloneField(field){ return Object.assign({}, field || {}); }
  function isConfirmationStep(step){
    const id = String((step && step.id) || '').toLowerCase();
    const title = String((step && step.title) || '').toLowerCase();
    return /confirm|acknowledg|boundary/.test(id) || /confirm|acknowledg|boundary/.test(title);
  }
  function isSupportStep(step){
    const id = String((step && step.id) || '').toLowerCase();
    const title = String((step && step.title) || '').toLowerCase();
    return /attachment|document|supporting|reference|upload|links/.test(id) || /attachment|document|supporting|reference|upload|links/.test(title);
  }
  function compactConfirmationHeading(text){
    const t = String(text || '').toLowerCase();
    if(/unpaid|unclear|misrouted|routed|route correction|correct product/.test(t)) return 'Routing may be corrected';
    if(/guarantee|availability|approval|outcome|access|pricing|allocation|delivery|brand decision/.test(t)) return 'No guaranteed outcome';
    if(/accurate|authorized|authority|best of my knowledge|submit it/.test(t)) return 'Accurate and authorized';
    if(/clarification|documents|authorization|quote|feasibility|lawful/.test(t)) return 'Further review may be required';
    if(/paid|payment|deposit|invoice|retainer|checkout/.test(t)) return 'Payment path acknowledged';
    return 'Confirmation required';
  }
  function polishConfirmationField(field){
    if(!field || field.__jsiiConfirmPolished) return field;
    if(field.type === 'checkbox'){
      const full = field.text || field.label || '';
      if(!field.text) field.text = full;
      field.summary_label = field.summary_label || compactConfirmationHeading(full || field.id);
      field.elite_confirmation = true;
      field.__jsiiConfirmPolished = true;
    }
    if(field.type === 'checkbox_group' && field.require_all){
      field.summary_label = field.summary_label || compactConfirmationHeading(field.label || field.id);
      field.elite_confirmation = true;
      field.__jsiiConfirmPolished = true;
    }
    return field;
  }
  function normalizeRecordForEngine(record){
    if(!record || record.__jsiiGlobalLawApplied) return record;
    record.steps = Array.isArray(record.steps) ? record.steps : [];
    let target = record.steps.find(step => isSupportStep(step) && !isConfirmationStep(step));
    const fieldsToAdd = UNIVERSAL_SUPPORT_FIELDS.map(cloneField);
    const legacySupportIds = new Set(['supporting_reference_links','supporting_upload_links','supporting_file_notes']);
    if(target){
      target.fields = Array.isArray(target.fields) ? target.fields : [];
      target.title = target.title || 'Supporting references / uploads';
      target.tone = 'Optional, but useful: add file-share links, screenshots, product pages, venue pages, PDF folders, route notes, or reference context before final confirmation.';
      target.fields = target.fields.filter(f => !legacySupportIds.has(f && f.id));
      target.fields.push.apply(target.fields, fieldsToAdd);
      target.system_added = target.system_added !== false;
      target.global_intake_law = true;
    } else {
      let insertAt = record.steps.length;
      for(let i=record.steps.length-1;i>=0;i--){ if(isConfirmationStep(record.steps[i])) { insertAt = i; break; } }
      record.steps.splice(insertAt, 0, {
        id:UNIVERSAL_SUPPORT_STEP_ID,
        title:'Supporting references / uploads',
        tone:'Optional, but useful: add file-share links, screenshots, product pages, venue pages, PDF folders, route notes, or reference context before final confirmation.',
        fields:fieldsToAdd,
        system_added:true,
        global_intake_law:true
      });
    }
    record.steps.forEach(step => {
      if(isConfirmationStep(step)){
        step.fields = Array.isArray(step.fields) ? step.fields.map(polishConfirmationField) : [];
      }
    });
    record.__jsiiGlobalLawApplied = true;
    return record;
  }
  function optionValueMatches(option, raw){
    const v = String(raw == null ? '' : raw);
    if(!v) return false;
    return [option && option.value, option && option.legacy_value, option && option.id, option && option.slug].some(x => String(x == null ? '' : x) === v);
  }
  function optionCanonicalValue(options, raw){
    const match = (options || []).find(o => optionValueMatches(o, raw));
    return match ? match.value : '';
  }
  function aliasesForField(field){
    const out = [];
    if(!field) return out;
    ['aliases','legacy_ids','source_ids','tally_ids'].forEach(k => { if(Array.isArray(field[k])) out.push.apply(out, field[k]); });
    ['tally_id','source_tally_qid','original_id','native_id'].forEach(k => { if(field[k]) out.push(field[k]); });
    return Array.from(new Set(out.filter(Boolean).map(String)));
  }
  function aliasValue(answers, keys){
    answers = answers || {};
    for(const key of keys){
      if(Object.prototype.hasOwnProperty.call(answers, key) && valuePresent(answers[key])) return answers[key];
    }
    return '';
  }
  function normalizeAnswerAliases(record, answers){
    if(!answers) return answers || {};
    const map = fieldMap(record);
    Object.keys(map).forEach(id => {
      if(valuePresent(answers[id])) return;
      const field = map[id];
      const v = aliasValue(answers, aliasesForField(field));
      if(valuePresent(v)) answers[id] = v;
    });
    if(!valuePresent(answers.client_name)) answers.client_name = aliasValue(answers, ['q7','Q7','full_name','lead_contact_name','client_full_name','requester_name','your_full_name']);
    if(!valuePresent(answers.contact_email)) answers.contact_email = aliasValue(answers, ['q8','Q8','best_contact_email','email','your_email_address','checkout_email','payment_email','email_used_at_checkout']);
    if(!valuePresent(answers.checkout_email)) answers.checkout_email = aliasValue(answers, ['payment_email','payment_email_address','email_used_at_checkout','q8','Q8']);
    if(!valuePresent(answers.payment_status)) answers.payment_status = aliasValue(answers, ['q4','Q4','payment_path','payment_review_status']);
    if(!valuePresent(answers.order_reference)) answers.order_reference = aliasValue(answers, ['q5','Q5','shopify_order_number','payment_reference','order_number']);
    if(!valuePresent(answers.urgency)) answers.urgency = aliasValue(answers, ['q24','Q24','how_urgent_is_this_request']);
    return answers;
  }
  function showIfMatches(showIf, answers){
    if(!showIf) return true;
    const field = showIf.field || showIf.id || showIf.answer_id;
    const op = String(showIf.operator || showIf.op || 'equals').toLowerCase();
    const expected = showIf.value;
    const actual = answers ? answers[field] : '';
    if(Array.isArray(actual)){
      if(op === 'includes' || op === 'contains') return actual.indexOf(expected) >= 0;
      if(op === 'not_equals') return actual.indexOf(expected) < 0;
      return actual.indexOf(expected) >= 0;
    }
    if(op === 'not_equals' || op === 'not') return String(actual) !== String(expected);
    if(op === 'includes' || op === 'contains') return String(actual || '').indexOf(String(expected)) >= 0;
    if(op === 'exists' || op === 'present') return valuePresent(actual);
    return String(actual) === String(expected);
  }
  function refreshConditionalFields(scope, record, state){
    normalizeAnswerAliases(record, state.answers || {});
    qsa(scope,'.jsi-field[data-show-if]').forEach(w=>{
      let showIf = null;
      try { showIf = JSON.parse(w.dataset.showIf || 'null'); } catch(e){}
      const visible = showIfMatches(showIf, state.answers || {});
      w.hidden = !visible;
      if(!visible){
        const id = w.dataset.fieldId;
        qsa(w,'input[type="checkbox"], input[type="radio"]').forEach(x=>{ x.checked = false; });
        const el = qs(w,'input:not([type="checkbox"]):not([type="radio"]), textarea, select');
        if(el) el.value = '';
        if(id) delete state.answers[id];
      }
    });
  }
  function fieldRequiredNow(field, state){
    let required = field && field.required === true;
    const logic = field && field.required_logic;
    if(logic === 'required_if_paid') required = isPaid(state);
    if(logic === 'required_if_urgent' || logic === 'urgent') required = isUrgent(state);
    return required;
  }
  function firstInvalidRecordStep(record, state){
    normalizeAnswerAliases(record, state.answers || {});
    const steps = record.steps || [];
    for(let si=0; si<steps.length; si++){
      const step = steps[si];
      for(const field of (step.fields || [])){
        if(field.show_if && !showIfMatches(field.show_if, state.answers || {})) continue;
        const answer = state.answers[field.id];
        if(fieldRequiredNow(field, state) && !valuePresent(answer)) return {step:si, field:field, message:'This field is required before continuing.'};
        if(valuePresent(answer) && isEmailField(record, field.id, {type:field.type}) && !isValidEmail(answer)) return {step:si, field:field, message:'Please enter a valid email address with one @ symbol and a real domain, such as name@example.com.'};
        if(field.require_all){
          const arr = Array.isArray(answer) ? answer : (answer ? [answer] : []);
          const opts = normalizeOptions(field, record);
          if(opts.length && arr.length < opts.length) return {step:si, field:field, message:'Please confirm every required acknowledgement before continuing.'};
        }
      }
    }
    return null;
  }

  function validateStep(root, record, state, container){
    Object.assign(state.answers, collectVisibleAnswers(root));
    normalizeAnswerAliases(record, state.answers);
    refreshConditionalFields(root, record, state);
    qsa(root,'.jsi-error-summary').forEach(el=>el.remove());
    const missing = [];
    let firstInvalid = null;
    qsa(root,'.jsi-field:not([hidden])').forEach(w=>{
      const id = w.dataset.fieldId;
      const answer = state.answers[id];
      const el = qs(w,'[data-answer-for]');
      const err = qs(w,'.jsi-error');
      let required = el && el.dataset.required === 'true';
      const logic = el && el.dataset.requiredLogic;
      if(logic === 'required_if_paid') required = isPaid(state);
      if(logic === 'required_if_urgent' || logic === 'urgent') required = isUrgent(state);
      let message = '';
      if(required && !valuePresent(answer)) message = 'This field is required before continuing.';
      else if(valuePresent(answer) && isEmailField(record, id, el) && !isValidEmail(answer)) message = 'Please enter a valid email address with one @ symbol and a real domain, such as name@example.com.';
      else if(el && el.dataset.requireAll === 'true'){
        const checks = qsa(w,'input[type="checkbox"]');
        const all = checks.length && checks.every(c=>c.checked);
        if(!all) message = 'Please confirm every required acknowledgement before submitting.';
      }
      if(message){
        if(!firstInvalid) firstInvalid = w;
        missing.push({id, label: getFieldLabel(record,id), message});
        if(err){ err.textContent = message; err.hidden = false; }
        w.classList.add('is-error');
        const answerEl = qs(w,'input, textarea, select, .jsi-options, .jsi-option');
        if(answerEl) answerEl.setAttribute('aria-invalid','true');
      } else {
        if(err) err.hidden = true;
        w.classList.remove('is-error');
        qsa(w,'input, textarea, select, .jsi-options, .jsi-option').forEach(x=>x.removeAttribute('aria-invalid'));
      }
    });
    if(missing.length){
      const summary = create('div','jsi-error-summary');
      summary.setAttribute('role','alert');
      summary.innerHTML = '<h4>Please complete the following before continuing:</h4><ul>'+missing.map(m=>'<li><button type="button" data-jsi-jump="'+esc(m.id)+'">'+esc(m.label)+'</button></li>').join('')+'</ul><p>We have moved you to the first item that needs attention.</p>';
      const nav = qs(root,'.jsi-nav');
      if(nav) root.insertBefore(summary, nav); else root.prepend(summary);
      qsa(summary,'[data-jsi-jump]').forEach(btn=>btn.addEventListener('click',()=>{
        const target = qs(root,'.jsi-field[data-field-id="'+CSS.escape(btn.getAttribute('data-jsi-jump'))+'"]');
        scrollToTarget(target || firstInvalid, 'smooth');
      }));
      blurActive();
      setTimeout(()=>scrollToTarget(firstInvalid || summary, 'smooth'), 80);
      return false;
    }
    return true;
  }

  const LABEL_OVERRIDES = {
    payment_status:'Payment Status',
    selected_product_path:'Selected Product Path',
    checkout_email:'Payment email address',
    payment_email:'Payment email address',
    order_reference:'Order / Payment Reference',
    client_name:'Client Name',
    contact_email:'Contact Email',
    support_requested:'Support Requested',
    final_confirmations:'Final Confirmations'
  };
  function displayFieldLabel(record, key, fallback){
    if(key === 'checkout_email' || key === 'payment_email') return 'Payment email address';
    return normalizeVisiblePaymentCopy(fallback || getFieldLabel(record,key));
  }
  function fieldMap(record){
    const map = {};
    (record.steps || []).forEach(step => (step.fields || []).forEach(field => { map[field.id] = field; }));
    return map;
  }

  function normalizeVisiblePaymentCopy(value){
    let t = String(value == null ? '' : value);
    t = t.replace(/Payment email address/gi,'Payment email address');
    t = t.replace(/Checkout email address/gi,'Payment email address');
    t = t.replace(/Checkout email, if already paid/gi,'Payment email address');
    t = t.replace(/Email used at payment checkout/gi,'Payment email address');
    t = t.replace(/payment email address/gi,'payment email address');
    t = t.replace(/payment email/gi,'payment email');
    t = t.replace(/checkout email/gi,'payment email');
    t = t.replace(/completed payment/gi,'completed payment');
    t = t.replace(/for payment and intake/gi,'for payment and intake');
    t = t.replace(/payment and intake/gi,'payment and intake');
    return t;
  }

  function normalizeCheckoutLabel(label){
    const t = String(label || '').trim();
    const old = [
      ['Checkout',' email address'].join(''),
      ['Checkout',' email, if already paid'].join(''),
      ['Payment Checkout',' email address'].join(''),
      ['Email used',' at payment checkout'].join('')
    ];
    return old.some(x => x.toLowerCase() === t.toLowerCase()) ? 'Payment email address' : normalizeVisiblePaymentCopy(t);
  }

  function displayFieldHelper(record, field){
    if(!field) return '';
    if(field.id === 'checkout_email' || field.id === 'payment_email') return 'Use the same email used for payment whenever possible.';
    if(field.id === 'order_reference' && !field.helper) return 'Include the order number, invoice number, receipt ID, access deposit reference, retainer note, or private payment reference if available.';
    return normalizeVisiblePaymentCopy(field.helper || '');
  }

  function getFieldLabel(record, key){
    const field = fieldMap(record)[key];
    const raw = LABEL_OVERRIDES[key] || (field && field.label) || key;
    return normalizeCheckoutLabel(stripHtml(raw).replace(/\s*\*\s*$/,'').trim());
  }
  function humanizeMachineText(value){
    const raw = String(value == null ? '' : value).trim();
    if(!raw) return '';
    if(/^https?:\/\//i.test(raw) || raw.indexOf('@') > 0) return raw;
    if(raw.indexOf('_') < 0 && !/^[a-z0-9-]+$/.test(raw)) return raw;
    let t = raw.replace(/[_-]+/g,' ').replace(/\s+/g,' ').trim();
    t = t.replace(/\b(japan|japansolved|vip|jdm|qa|pdf|url|usd|id)\b/gi, m => {
      const u = m.toUpperCase();
      return ['VIP','JDM','QA','PDF','URL','USD','ID'].indexOf(u) >= 0 ? u : (m.charAt(0).toUpperCase()+m.slice(1));
    });
    if(t && t === t.toLowerCase()) t = t.charAt(0).toUpperCase()+t.slice(1);
    return t;
  }
  function humanKey(key){ return LABEL_OVERRIDES[key] || humanizeMachineText(key).replace(/\b\w/g, s => s.toUpperCase()); }
  function emailValue(value){
    if(Array.isArray(value)) return value.filter(valuePresent).map(humanizeMachineText).join('; ');
    if(value === true) return 'Yes';
    if(value === false) return 'No';
    return humanizeMachineText(value);
  }
  function legacyDisplayMap(record, field){
    return Object.assign({}, (record && record.legacy_display_map) || {}, (field && field.legacy_display_map) || {}, (field && field.display_map) || {});
  }
  function optionDisplayLabel(record, field, raw){
    const v = String(raw == null ? '' : raw).trim();
    if(!v) return '';
    const map = legacyDisplayMap(record, field);
    if(map[v]) return map[v];
    const opts = normalizeOptions(field || {}, record || {});
    const match = opts.find(o => optionValueMatches(o, v));
    if(match) return cleanRouteLabel(match.label || match.value || v);
    return humanizeMachineText(v);
  }
  function displayAnswer(value, type){
    const v = emailValue(value);
    if(v) return v;
    if(type === 'checkbox_group' || type === 'radio' || type === 'select') return 'No selection';
    if(type === 'url' || type === 'url_list' || /link|url|upload|file/i.test(type || '')) return 'No links provided';
    return 'No details';
  }
  function displayFieldAnswer(record, field, value){
    if(field && field.id === 'selected_product_path') return routeDisplay(record,value);
    if(Array.isArray(value)){
      const parts = value.filter(valuePresent).map(v => optionDisplayLabel(record, field, v)).filter(Boolean);
      return parts.length ? parts.join('; ') : displayAnswer(value, field && field.type);
    }
    if(field && /^(radio|select|checkbox_group)$/.test(String(field.type || ''))){
      return optionDisplayLabel(record, field, value) || displayAnswer(value, field && field.type);
    }
    if(field && field.type === 'checkbox') return value ? (field.text ? stripHtml(field.text) : 'Confirmed') : 'Not confirmed';
    return displayAnswer(value, field && field.type);
  }
  function addLine(lines,label,value,blankText){
    const v = emailValue(value) || blankText || 'No details';
    lines.push(label+':'); lines.push(v); lines.push('');
  }
  function serviceTopic(record){ return String(record.page_title || record.intake_title || record.page_handle || 'JapanSolved™ Request').replace(/ Desk™$/,'').replace(/™$/,''); }

  function visibleIntakeTitle(record){
    const raw = String((record && (record.intake_title || record.page_title)) || 'JapanSolved™ Intake').trim();
    if(!raw) return 'JapanSolved™ Intake Form';
    if(/\bForm\s*$/i.test(raw)) return raw;
    return raw + ' Form';
  }


  function productTitleFromRoute(route){
    const raw = String(route || '').replace(/^https?:\/\/[^/]+/,'').replace(/^\/products\//,'').replace(/^\/pages\//,'');
    if(!raw) return 'No payment path supplied';
    return raw.split(/[-_]+/).filter(Boolean).map(word => {
      if(word.length <= 2 && /^(qa|vip|jdm)$/i.test(word)) return word.toUpperCase();
      return word.charAt(0).toUpperCase()+word.slice(1);
    }).join(' ')+'™';
  }

  function routeDisplay(record,value){
    const v = String(value || '').trim();
    if(!v) return 'No selection';
    if(v === 'not_sure') return 'I am not sure';
    const match = routeObjects(record).find(obj => String(obj.route || obj.value || '') === v || String(obj.slug_or_note || '') === v);
    const title = match ? (match.title || match.product_title || productTitleFromRoute(match.route || v)) : productTitleFromRoute(v);
    if(v.indexOf('/products/') >= 0 || v.indexOf('/pages/') >= 0) return title+' — '+v;
    return title || v;
  }

  function deskCode(record){
    const text = String((record && (record.page_handle || record.page_title || record.intake_title)) || '').toLowerCase();
    if(text.indexOf('medical') >= 0) return 'MED';
    if(text.indexOf('logistics') >= 0 || text.indexOf('cargo') >= 0) return 'LOG';
    if(text.indexOf('sourcing') >= 0 || text.indexOf('buyer') >= 0 || text.indexOf('procurement') >= 0) return 'SRC';
    if(text.indexOf('travel') >= 0 || text.indexOf('concierge') >= 0 || text.indexOf('experience') >= 0) return 'CNC';
    if(text.indexOf('jdm') >= 0) return 'JDM';
    if(text.indexOf('lost') >= 0 || text.indexOf('missing') >= 0) return 'RCV';
    return 'INT';
  }

  function compactDate(){
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth()+1).padStart(2,'0');
    const day = String(d.getDate()).padStart(2,'0');
    return ''+y+m+day;
  }

  function randomToken(len){
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let out = '';
    for(let i=0;i<(len || 4);i++) out += chars[Math.floor(Math.random()*chars.length)];
    return out;
  }

  function buildCaseId(record){
    return 'JSI-'+deskCode(record)+'-'+compactDate()+'-'+randomToken(4);
  }

  function answerForEmail(record, field, value){
    return displayFieldAnswer(record, field, value);
  }

  function boundaryLine(record,payload){
    const text = String((record && (record.page_title || record.page_handle || '')) || '').toLowerCase();
    if(text.indexOf('medical') >= 0) return 'Non-medical logistics only; medical decisions, diagnosis, treatment, emergency response, prescriptions, and clinical interpretation are excluded.';
    if(text.indexOf('companion') >= 0 || text.indexOf('nightlife') >= 0) return 'Private concierge support only; lawful boundaries, suitability review, and safety routing apply.';
    return 'Paid intake review only; acceptance, feasibility, pricing, availability, and outcome are not guaranteed.';
  }

  function priorityLine(payload){
    const u = String(payload.urgency || '').toLowerCase();
    if(u.indexOf('24 hour') >= 0 || u.indexOf('overdue') >= 0 || u.indexOf('already committed') >= 0) return 'URGENT / same-day operator attention recommended';
    if(u.indexOf('72 hour') >= 0 || u.indexOf('1 week') >= 0 || u.indexOf('within 1 week') >= 0) return 'HIGH / calendar-sensitive review';
    if(u.indexOf('2 week') >= 0 || u.indexOf('1 month') >= 0) return 'NORMAL-PRIORITY / scheduled review';
    return 'STANDARD / classify after payment verification';
  }

  function responseTarget(payload){
    const u = String(payload.urgency || '').toLowerCase();
    if(u.indexOf('24 hour') >= 0 || u.indexOf('overdue') >= 0) return 'Verify payment and triage as soon as possible.';
    if(u.indexOf('72 hour') >= 0 || u.indexOf('1 week') >= 0) return 'Verify payment and triage within the next operating window.';
    return 'Verify payment, then proceed through normal review queue.';
  }

  function weakAnswerFlags(record,payload){
    const ans = payload.answers || {};
    const map = fieldMap(record);
    const important = ['plain_language_goal','target_dates','city_area','privacy_authority','support_requested','budget_range','cost_authorization'];
    const flags = [];
    important.forEach(id => {
      const raw = emailValue(ans[id]);
      const field = map[id];
      if(!field) return;
      const compact = raw.replace(/\s+/g,' ').trim();
      const words = compact ? compact.split(/\s+/).length : 0;
      if(!compact || /^yes$|^no$|^n\/a$|^na$|^none$|^me$/i.test(compact) || words <= 1){
        flags.push(getFieldLabel(record,id)+' appears short, vague, or missing.');
      }
    });
    if(String(ans.provider_status || '').toLowerCase().indexOf('researching') >= 0) flags.push('Provider or appointment appears not yet selected; feasibility may require clarification.');
    if(String(ans.selected_product_path || '') === 'not_sure') flags.push('Selected payment path is uncertain.');
    return flags;
  }

  function riskFlags(record,payload){
    const ans = payload.answers || {};
    const flags = [];
    if(priorityLine(payload).indexOf('URGENT') >= 0) flags.push('Urgent timing claimed.');
    if(!payload.order_reference && /yes|completed|secured|retainer|instructed/i.test(String(payload.payment_status || ''))) flags.push('Payment claimed but order/payment reference missing.');
    if(String(ans.provider_status || '').toLowerCase().indexOf('researching') >= 0) flags.push('Provider or appointment not confirmed.');
    if(emailValue(ans.support_requested).toLowerCase().indexOf('not sure') >= 0) flags.push('Support lane includes “Not sure.”');
    const page = String(record.page_title || record.page_handle || '').toLowerCase();
    if(page.indexOf('medical') >= 0) flags.push('Medical-adjacent file: keep strictly non-medical.');
    const weak = weakAnswerFlags(record,payload);
    if(weak.length) flags.push('Clarification likely required for short or weak answers.');
    return Array.from(new Set(flags));
  }

  function recommendedActions(record,payload){
    const actions = ['Verify payment/order reference against Shopify orders or invoice records.'];
    const ans = payload.answers || {};
    if(String(ans.provider_status || '').toLowerCase().indexOf('researching') >= 0) actions.push('Ask whether the client already has a provider, clinic, appointment, or only wants logistics classification.');
    if(weakAnswerFlags(record,payload).length) actions.push('Request clarification for vague, one-word, or missing answers before quoting expanded work.');
    if(priorityLine(payload).indexOf('URGENT') >= 0) actions.push('Check calendar feasibility before promising response speed, attendance, interpretation, or logistics availability.');
    actions.push('Confirm boundary and route the file to paid review, deposit, retainer, specialist review, or payment-route correction.');
    return actions;
  }

  function buildEmailSubject(record,payload){
    const id = payload.case_id || payload.submission_id || buildCaseId(record);
    const client = String(payload.client_name || 'Client').trim();
    const service = serviceTopic(record).replace(/^Japan\s+/i,'').replace(/\s+Desk$/i,'').trim() || 'Intake';
    const urgency = String(payload.urgency || 'New Intake').trim();
    return '['+id+'] '+service+' Intake — '+client+' — '+urgency;
  }

  function disableShopifyExtraContactFields(form){
    if(!form) return;
    const selectors = [
      '[data-jsi-admin-summary]',
      '[data-jsi-admin-html]',
      '[data-jsi-client-copy]',
      '[data-jsi-version]',
      '[data-jsi-contact-subject]',
      'input[name="contact[subject]"]',
      'input[name="contact[tags]"]',
      'textarea[name="contact[admin_case_summary]"]',
      'textarea[name="contact[client_receipt_copy]"]',
      'input[name="contact[intake_engine_version]"]',
      'input[name="contact[engine_version]"]',
      'textarea[name="contact[admin_case_html]"]'
    ];
    qsa(form, selectors.join(',')).forEach(el => {
      el.value = '';
      el.disabled = true;
      el.removeAttribute('name');
      el.setAttribute('aria-hidden','true');
    });
  }

  function trimForFieldName(label){
    let text = stripHtml(label || '')
      .replace(/[\[\]{}<>]/g,' ')
      .replace(/\s+/g,' ')
      .trim();
    if(!text) text = 'Intake Field';
    if(text.length > 82) text = text.slice(0,79).replace(/\s+\S*$/,'') + '...';
    return text;
  }

  function emptyOrNoise(value){
    const text = String(value == null ? '' : value).replace(/\s+/g,' ').trim();
    return !text || /^no details(?: provided)?$/i.test(text) || /^not confirmed$/i.test(text);
  }

  function ensureStructuredContainer(form){
    let holder = qs(form,'[data-jsi-structured-fields]');
    if(!holder){
      holder = document.createElement('div');
      holder.className = 'jsi-hidden-payload';
      holder.setAttribute('data-jsi-structured-fields','');
      holder.setAttribute('aria-hidden','true');
      form.appendChild(holder);
    }
    return holder;
  }

  function clearStructuredFields(form){
    const holder = ensureStructuredContainer(form);
    holder.innerHTML = '';
    return holder;
  }

  function appendShopifyField(holder, label, value, seen){
    if(!holder || emptyOrNoise(value)) return;
    let clean = trimForFieldName(label);
    seen = seen || {};
    if(seen[clean]){
      seen[clean] += 1;
      clean = trimForFieldName(clean + ' ' + seen[clean]);
    } else {
      seen[clean] = 1;
    }
    const text = String(value == null ? '' : value).trim();
    const field = text.length > 130 || /\n/.test(text) ? document.createElement('textarea') : document.createElement('input');
    field.className = 'jsi-hidden-payload';
    field.setAttribute('data-jsi-structured-field','');
    field.setAttribute('aria-hidden','true');
    field.tabIndex = -1;
    field.name = 'contact[' + clean + ']';
    if(field.tagName === 'TEXTAREA') field.value = text;
    else { field.type = 'hidden'; field.value = text; }
    holder.appendChild(field);
  }

  function minimalContactBody(record,payload){
    const lines = [];
    lines.push('JapanSolved™ native intake submitted.');
    lines.push('');
    lines.push('Case ID: ' + (payload.case_id || payload.submission_id || buildCaseId(record)));
    lines.push('Desk: ' + (record.page_title || payload.page_title || payload.page_handle || 'JapanSolved Intake'));
    lines.push('Client: ' + (payload.client_name || 'No client name supplied'));
    lines.push('Best contact email: ' + (payload.contact_email || payload.checkout_email || 'No email supplied'));
    lines.push('Payment email: ' + (payload.checkout_email || 'No payment email supplied'));
    lines.push('Order / payment reference: ' + (payload.order_reference || 'No order reference supplied'));
    lines.push('Submitted JST: ' + (payload.submitted_at_jst || nowJST()));
    lines.push('');
    lines.push('Structured intake fields are included in this Shopify contact notification. Use those readable fields as the primary internal review layer.');
    return lines.join('\n');
  }

  function populateShopifyStructuredFields(form, record, model, payload){
    const holder = clearStructuredFields(form);
    const seen = {};
    appendShopifyField(holder,'Case ID', payload.case_id || payload.submission_id || buildCaseId(record), seen);
    appendShopifyField(holder,'Submission Time JST', payload.submitted_at_jst || nowJST(), seen);
    appendShopifyField(holder,'Intake Engine Version', ENGINE_VERSION, seen);
    appendShopifyField(holder,'Page / Desk', record.page_title || payload.page_title || '', seen);
    appendShopifyField(holder,'Page Handle', record.page_handle || payload.page_handle || '', seen);
    appendShopifyField(holder,'Page URL', record.page_url || payload.page_url || '', seen);
    appendShopifyField(holder,'Client Name', payload.client_name || '', seen);
    appendShopifyField(holder,'Best Contact Email', payload.contact_email || '', seen);
    appendShopifyField(holder,'Payment Email', payload.checkout_email || '', seen);
    appendShopifyField(holder,'Order / Payment Reference', payload.order_reference || '', seen);
    appendShopifyField(holder,'Payment Status', payload.payment_status || '', seen);
    appendShopifyField(holder,'Selected Product / Payment Path', payload.selected_product_path || '', seen);
    appendShopifyField(holder,'Urgency', payload.urgency || '', seen);
    appendShopifyField(holder,'Routing Tags', Array.isArray(payload.routing_tags) ? payload.routing_tags.join('; ') : '', seen);

    (model.sections || []).forEach((section, idx)=>{
      appendShopifyField(holder, 'Section ' + String(idx + 1).padStart(2,'0'), section.title || ('Step ' + (idx+1)), seen);
      (section.items || []).forEach(item=>{
        appendShopifyField(holder, item.label || item.id || 'Intake Field', item.value || '', seen);
      });
    });

    const flags = riskFlags(record,payload);
    if(flags.length) appendShopifyField(holder,'Internal Flags', flags.join('\n'), seen);
    const actions = recommendedActions(record,payload);
    if(actions.length) appendShopifyField(holder,'Recommended Internal Actions', actions.join('\n'), seen);
  }


  function compilePayload(record, state){
    const routeTags = [];
    const ans = state.answers;
    normalizeAnswerAliases(record, ans);
    if(!isPaid(state)) routeTags.push(isUnsurePayment(state) ? 'PAYMENT_ROUTE_UNSURE' : 'UNPAID_OR_UNMATCHED_PAYMENT_ROUTE');
    if(isUrgent(state)) routeTags.push('URGENT_REVIEW');
    if(Array.isArray(ans.hub_risks)) routeTags.push.apply(routeTags, ans.hub_risks.map(slug));
    if(Array.isArray(ans.related_desks)) routeTags.push.apply(routeTags, ans.related_desks.map(slug));
    const caseId = state.case_id || buildCaseId(record);
    state.case_id = caseId;
    const selectedPath = aliasValue(ans, ['selected_product_path','desired_pathway','default_product_path','q6','Q6']);
    const paymentStatus = aliasValue(ans, ['payment_status','payment_path','payment_review_status','q4','Q4']);
    const checkoutEmail = aliasValue(ans, ['checkout_email','payment_email','payment_email_address','email_used_at_checkout','q8','Q8']);
    const orderRef = aliasValue(ans, ['order_reference','payment_reference','shopify_order_number','order_number','q5','Q5']);
    const clientName = aliasValue(ans, ['client_name','lead_contact_name','full_name','requester_name','q7','Q7']);
    const contactEmail = aliasValue(ans, ['contact_email','best_contact_email','your_email_address','email','q8','Q8','checkout_email','payment_email']);
    const urgency = aliasValue(ans, ['urgency','request_urgency','how_urgent_is_this_request','q24','Q24']);
    return {
      submission_id:caseId, case_id:caseId, submitted_at_jst:nowJST(), engine_version:ENGINE_VERSION,
      phase:'phase_1_native_contact_delivery', page_sequence:record.page_sequence, record_id:record.record_id, page_title:record.page_title, page_handle:record.page_handle,
      page_url:record.page_url, service_family:record.service_family || '', intake_version:record.intake_version, payment_policy:record.payment_policy,
      selected_product_path:selectedPath || '', payment_status:paymentStatus || '', checkout_email:checkoutEmail || '', order_reference:orderRef || '',
      client_name:clientName || '', contact_email:contactEmail || checkoutEmail || '', urgency:urgency || '', routing_tags:Array.from(new Set(routeTags)).filter(Boolean), answers:ans
    };
  }

  function statusBadge(payload){
    const raw = String(payload.payment_status || '').toLowerCase();
    if(raw.indexOf('yes') >= 0 || raw.indexOf('completed') >= 0 || raw.indexOf('secured') >= 0 || raw.indexOf('instructed') >= 0) return 'Paid path declared / match required';
    if(raw.indexOf('not sure') >= 0) return 'Payment route uncertain / classify first';
    if(raw) return 'Payment not yet matched';
    return 'Payment status not supplied';
  }

  function confirmationSummary(payload){
    const ans = payload.answers || {};
    const text = emailValue(ans.final_confirmations).toLowerCase();
    const pageText = String(payload.page_title || payload.page_handle || '').toLowerCase();
    if(pageText.indexOf('ticket') >= 0 || pageText.indexOf('event') >= 0 || pageText.indexOf('entertainment') >= 0){
      return [
        'Paid review, deposit, invoice, retainer, event-day support path, or approved payment path acknowledged',
        'Unpaid or unclear submissions may be routed to the correct product or payment path before processing',
        'No guarantee of ticket availability, lottery success, venue approval, seller cooperation, transferability, event-day entry, timing, access, or outcome',
        'Information accuracy, authorization, and safe review permission confirmed',
        'Official-route limits, availability windows, identity or pickup rules, lawful handling requirements, and possible added clarification, authorization, payment, or quote accepted'
      ];
    }
    const items = [
      'Paid review / deposit / approved payment path acknowledged',
      'Route correction may be required before review or action',
      'No guaranteed acceptance, approval, access, clearance, cooperation, pricing, or outcome',
      'Information accuracy and submission authority confirmed',
      'Additional clarification, documents, payment, authorization, or quote may be requested'
    ];
    if(pageText.indexOf('medical') >= 0 || text.indexOf('medical advice') >= 0){
      items.push('Medical-service boundary accepted: no medical advice, diagnosis, treatment recommendation, emergency response, prescription handling, clinical interpretation, or clinical decision-making');
    }
    return items;
  }

  function buildCaseFileModel(record, payload){
    const ans = payload.answers || {};
    normalizeAnswerAliases(record, ans);
    const sections = [];
    (record.steps || []).forEach((step, idx) => {
      let items = [];
      if(isConfirmationStep(step)){
        items = [{id:'final_confirmation_summary', label:'Final confirmations', value:confirmationSummary(payload).map(x=>'✓ '+x).join('\n'), type:'confirmation_summary', required:true}];
      } else {
        (step.fields || []).forEach(field => {
          if(field.show_if && !showIfMatches(field.show_if, ans)) return;
          items.push({id:field.id, label:getFieldLabel(record,field.id), value:answerForEmail(record, field, ans[field.id]), type:field.type, required:!!field.required});
        });
      }
      if(items.length) sections.push({id:step.id, title: step.title || ('Step '+(idx+1)), tone: step.tone || '', items});
    });
    return {
      payload,
      topic: serviceTopic(record),
      snapshot:[
        {label:'Client', value:payload.client_name || 'No details'},
        {label:'Contact Email', value:payload.contact_email || payload.checkout_email || 'No details'},
        {label:'Service / Topic', value:record.page_title || 'No details'},
        {label:'Payment Route', value:answerForEmail(record, fieldMap(record).payment_status || {type:'text'}, payload.payment_status) || 'No details'},
        {label:'Urgency', value:answerForEmail(record, fieldMap(record).urgency || {type:'text'}, payload.urgency) || 'No details'},
        {label:'Submitted', value:payload.submitted_at_jst || nowJST()}
      ],
      sections
    };
  }

  function renderReviewHtml(model){
    const snapshot = model.snapshot.map(i=>'<div class="jsi-review-kv"><strong>'+esc(i.label)+'</strong><span>'+esc(i.value)+'</span></div>').join('');
    const renderItem = item => {
      const value = String(item.value || '').trim();
      if(item.id === 'final_confirmation_summary' || item.type === 'confirmation_summary'){
        const checks = value.split(/\n+/).map(x=>x.replace(/^✓\s*/,'').trim()).filter(Boolean);
        return '<div class="jsi-review-confirm-card"><dt>'+esc(item.label)+'</dt><dd><ul class="jsi-review-checks">'+checks.map(x=>'<li><span aria-hidden="true">✓</span><b>'+esc(x)+'</b></li>').join('')+'</ul></dd></div>';
      }
      return '<div class="jsi-review-row"><dt>'+esc(item.label)+'</dt><dd>'+esc(value || 'No details').replace(/\n/g,'<br>')+'</dd></div>';
    };
    const sections = model.sections.map(sec=>{
      const rows = sec.items.map(renderItem).join('');
      return '<section class="jsi-review-section"><h4>'+esc(sec.title)+'</h4><dl>'+rows+'</dl></section>';
    }).join('');
    return '<div class="jsi-review"><p class="jsi-step-count">Review before sending</p><h3>Review your intake details</h3><p class="jsi-review-intro">Please inspect the formatted case file below before sending it to JapanSolved™. If anything is missing, unclear, or important to add, go back and edit the details before submission.</p><div class="jsi-review-snapshot">'+snapshot+'</div>'+sections+'</div>';
  }

  function clientReceiptHtml(model){
    const sectionNames = {
      payment_gate:'Payment and route',
      identity:'Client details',
      visit_profile:'Visit profile',
      appointment_details:'Appointment and schedule',
      support_needs:'Support requested',
      documents_context:'Documents and context',
      budget_scope:'Budget and authorization',
      final_confirm:'Final confirmations'
    };
    const priorityIds = new Set(['payment_status','selected_product_path','checkout_email','order_reference','client_name','contact_email','country_region','visit_type','provider_status','city_area','target_dates','urgency','support_requested','budget_range','cost_authorization','digital_signature']);
    const isEmpty = v => !String(v || '').trim();
    const cleanValue = v => String(v == null ? '' : v).trim();
    const renderValue = item => {
      const value = cleanValue(item.value);
      if(isEmpty(value)) return '<span class="jsi-receipt-empty">No details</span>';
      if(item.id === 'final_confirmations' || value.indexOf('✓ ') >= 0){
        const checks = value.split(/\n+/).map(x=>x.replace(/^✓\s*/,'').trim()).filter(Boolean);
        return '<ul class="jsi-receipt-checks">'+checks.map(x=>'<li><span aria-hidden="true">✓</span><b>'+esc(x)+'</b></li>').join('')+'</ul>';
      }
      if(item.type === 'url' && (/no links/i).test(value)) return '<span class="jsi-receipt-empty">No links provided</span>';
      return '<span class="jsi-receipt-value-text">'+esc(value).replace(/\n/g,'<br>')+'</span>';
    };
    const snapshot = (model.snapshot || []).filter(i=>['Client','Contact Email','Service / Topic','Payment Route','Urgency','Submitted'].indexOf(i.label) >= 0).map(i=>{
      return '<div class="jsi-receipt-snapshot-item"><span>'+esc(i.label)+'</span><strong>'+esc(i.value || 'No details')+'</strong></div>';
    }).join('');
    const sections = (model.sections || []).map(sec=>{
      const rows = (sec.items || []).map(item=>{
        const priority = priorityIds.has(item.id) ? ' is-priority' : '';
        return '<div class="jsi-receipt-row'+priority+'"><dt>'+esc(item.label)+'</dt><dd>'+renderValue(item)+'</dd></div>';
      }).join('');
      if(!rows) return '';
      return '<section class="jsi-receipt-section"><div class="jsi-receipt-section-head"><h4>'+esc(sectionNames[sec.id] || sec.title || 'Submitted details')+'</h4><span class="jsi-receipt-line" aria-hidden="true"></span></div><dl>'+rows+'</dl></section>';
    }).join('');
    return '<div class="jsi-submitted-details jsi-client-receipt"><div class="jsi-receipt-heading"><p class="jsi-success-kicker">Submitted receipt</p><h3>These are the details you submitted.</h3><p>Your intake details are saved below as a client-facing receipt. JapanSolved™ will use the full internal case file for review and follow-up.</p></div><div class="jsi-receipt-snapshot">'+snapshot+'</div>'+sections+'</div>';
  }

  function payloadToEmail(payload, record){
    record = record || findRecord(payload.page_handle) || {steps:[]};
    const model = buildCaseFileModel(record,payload);
    const lines=[];
    const subject = buildEmailSubject(record,payload);
    const flags = riskFlags(record,payload);
    const weak = weakAnswerFlags(record,payload);
    const actions = recommendedActions(record,payload);
    const ans = payload.answers || {};

    lines.push(subject);
    lines.push('');
    lines.push('JAPANSOLVED™ PRIVATE INTAKE CASE BRIEF');
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    lines.push('');
    lines.push('CASE ID');
    lines.push(payload.case_id || payload.submission_id || 'No case ID');
    lines.push('');
    lines.push('SERVICE');
    lines.push(record.page_title || serviceTopic(record));
    lines.push('');
    lines.push('CLIENT');
    lines.push(payload.client_name || 'No details');
    lines.push('');
    lines.push('CONTACT EMAIL');
    lines.push(payload.contact_email || payload.checkout_email || 'No details');
    lines.push('');
    lines.push('OPERATOR TRIAGE');
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    addLine(lines,'Priority',priorityLine(payload));
    addLine(lines,'Response Target',responseTarget(payload));
    addLine(lines,'Payment Claim',statusBadge(payload));
    addLine(lines,'Selected Payment Path',routeDisplay(record,payload.selected_product_path));
    addLine(lines,'Payment Checkout Email',payload.checkout_email || 'No details');
    addLine(lines,'Order / Payment Reference',payload.order_reference || 'No details');
    addLine(lines,'Boundary',boundaryLine(record,payload));

    lines.push('RECOMMENDED NEXT ACTIONS');
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    actions.forEach((x,i)=>lines.push((i+1)+'. '+x));
    lines.push('');

    lines.push('RISK / CLARIFICATION FLAGS');
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    if(flags.length) flags.forEach(x=>lines.push('⚠ '+x));
    else lines.push('No immediate risk flags generated from the submitted answers.');
    if(weak.length){
      lines.push('');
      lines.push('Short-answer review:');
      weak.forEach(x=>lines.push('- '+x));
    }
    lines.push('');

    lines.push('CLIENT SNAPSHOT');
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    addLine(lines,'Client',payload.client_name || 'No details');
    addLine(lines,'Country / Region',ans.country_region || 'No details');
    addLine(lines,'Client Role',ans.client_role || 'No details');
    addLine(lines,'Preferred Contact Method',ans.contact_method || 'No details');
    addLine(lines,'Urgency',payload.urgency || 'No details');
    addLine(lines,'Submitted',payload.submitted_at_jst || nowJST());

    lines.push('SUBMITTED DETAILS');
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    model.sections.forEach((sec,idx)=>{
      lines.push('');
      lines.push((idx+1)+'. '+String(sec.title || 'Details').toUpperCase());
      lines.push('────────────────────────────────────────');
      sec.items.forEach(item=>addLine(lines,item.label,item.value));
    });

    lines.push('END OF CASE BRIEF');
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    lines.push('Prepared by JapanSolved™ Native Intake.');
    return lines.join('\n');
  }

  function clientReceiptPlainText(model){
    const lines=[];
    lines.push('Thank you. Your intake has been received.');
    lines.push('');
    lines.push('Your submission has been recognized and prepared for JapanSolved™ review. Our team will match your payment route and intake details, assess your request, and contact you directly by email with the next steps as soon as we can.');
    lines.push('');
    lines.push('Please watch your email for our reply. If you do not receive anything within 3 days, check your spam or promotions folder first. You may also send us a follow-up message through the Follow-Up on Existing Request link in the footer below.');
    lines.push('');
    lines.push('Thank you for trusting JapanSolved™. We look forward to serving your request with care, structure, and practical solutions.');
    lines.push('');
    lines.push('THESE ARE THE DETAILS YOU SUBMITTED');
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    model.sections.forEach((sec,idx)=>{
      lines.push(''); lines.push((idx+1)+'. '+String(sec.title || 'Details').toUpperCase());
      sec.items.forEach(item=>{ lines.push(item.label+':'); lines.push(item.value || 'No details'); lines.push(''); });
    });
    lines.push('If you need to add something more, correct something, or mention something you forgot, please send us a follow-up here:');
    lines.push(FOLLOWUP_URL);
    return lines.join('\n');
  }

  function renderPaymentRoutes(record){
    const panel = create('div','jsi-payment-routes');
    const paymentRoutes = routeObjects(record).filter(obj => obj.render_as_link !== false && obj.route);
    const paymentCount = paymentRoutes.length;
    const recordHandle = String(record && record.page_handle || '').trim();
    if(recordHandle) panel.setAttribute('data-jsi-payment-handle', recordHandle);
    panel.setAttribute('data-jsi-payment-count', String(paymentCount));
    panel.classList.add('jsi-payment-count-' + paymentCount);
    if(paymentCount >= 4) panel.classList.add('jsi-payment-grid-eligible');
    if(recordHandle === 'japan-private-travel-companion-sabbatical-reset'){
      panel.classList.add('jsi-payment-single-column-lock','jsi-payment-private-companion-lock');
    }
    const linkItems = paymentRoutes.map((obj, idx) => {
      if(obj.status === 'verify_before_hardwire') return '<li><span class="jsi-route-pending">Payment route pending confirmation: '+esc(obj.slug_or_note)+'</span></li>';
      const label = normalizeVisiblePaymentCopy(obj.label || inferRouteLabel(obj.route, idx));
      return '<li><a href="'+esc(obj.route)+'">'+esc(label)+'</a></li>';
    }).join('');
    const privateRoute = routeObjects(record).find(obj => obj.render_as_link === false || obj.value === 'approved_private_payment_path');
    const privateLabel = privateRoute ? (privateRoute.label || 'Approved private payment path') : 'Approved private payment path';
    const routeList = linkItems ? '<ul class="jsi-payment-route-list">'+linkItems+'</ul>' : '';
    const title = record.payment_block_title || 'Complete payment before continuing';
    const copy = record.payment_block_copy || 'Choose the public payment path that matches this request.';
    const fineprint = record.payment_block_fineprint || 'Use the same payment email at intake so the file can be matched.';
    const privateLabelText = esc(privateLabel).replace(/Approved invoice \/ access deposit \/ custom quote \/ retainer/i,'Approved private payment path');
    panel.innerHTML = '<div class="jsi-payment-instruction jsi-payment-copy-rail"><h4 class="jsi-payment-heading jsi-shine-heading">'+esc(title)+'</h4><p class="jsi-payment-subtext jsi-payment-copy-rail">'+esc(copy)+'</p></div>'+routeList+'<div class="jsi-private-route-note jsi-payment-copy-rail"><strong class="jsi-private-route-title">Already approved privately?</strong><p class="jsi-private-route-copy">Select “'+privateLabelText+'” in the dropdown above, then enter the invoice, access deposit, retainer, custom quote, or payment reference so the file can be matched.</p></div><p class="jsi-fineprint jsi-payment-helper-note jsi-payment-copy-rail">'+esc(fineprint)+'</p>';
    return panel;
  }

  function renderCaptchaNotice(kind){
    const cls = kind === 'review' ? 'jsi-captcha-brief jsi-captcha-review-brief' : 'jsi-captcha-brief jsi-captcha-step-brief';
    const body = kind === 'review'
      ? '<strong>Secure submission notice:</strong> After you click the final send button, you may be routed to a brief CAPTCHA or human-check screen before the intake is delivered. This is normal secure contact-form protection and helps keep your information safe while it is submitted through our secure intake route.'
      : '<strong>Secure submission notice:</strong> The final send step may open a brief CAPTCHA or human-check screen before the intake is delivered.';
    return create('div', cls, body);
  }

  function showSubmissionProblem(root, message){
    if(!root) return;
    qsa(root,'.jsi-submit-problem').forEach(el=>el.remove());
    const box = create('div','jsi-submit-problem jsi-error-summary');
    box.setAttribute('role','alert');
    box.innerHTML = '<h4>Submission did not leave the page yet.</h4><p>'+esc(message || 'Please check the highlighted fields and try again. If a secure verification screen appears, complete it to finish sending the intake.')+'</p>';
    const nav = qs(root,'.jsi-review-nav, .jsi-nav');
    if(nav && nav.parentNode) nav.parentNode.insertBefore(box, nav);
    else root.appendChild(box);
    scrollToTarget(box,'smooth');
  }

  function startSubmitWatchdog(container, form, root){
    const token = Date.now() + ':' + Math.random().toString(36).slice(2);
    form.setAttribute('data-jsi-submit-watchdog', token);
    setTimeout(function(){
      if(!form || form.getAttribute('data-jsi-submit-watchdog') !== token) return;
      form.__jsiiSubmitting = false;
      form.removeAttribute('data-jsi-submit-prepared');
      form.classList.remove('is-submitting');
      if(container) container.classList.remove('jsi-submission-lock');
      qsa(form,'[data-jsi-temp-disabled]').forEach(btn=>{ btn.removeAttribute('disabled'); btn.removeAttribute('data-jsi-temp-disabled'); });
      const stillHere = document.body && document.body.contains(form);
      if(stillHere){
        showSubmissionProblem(root || qs(form,'[data-jsi-root]') || form, 'The secure contact handoff did not complete from this screen. Please verify the contact emails, keep this page open, and try Send Intake Details again. If the human-check screen appears, complete it before closing the page.');
      }
    }, 12000);
  }

  function renderSuccess(container, root, record, model){
    setIntakeFrameMode(container, 'success');
    const details = model ? clientReceiptHtml(model) : '<div class="jsi-submitted-details"><h3>These are the details you submitted</h3><p class="jsi-muted-note">The submitted details were received. If you need to add or correct anything, please use the follow-up link below.</p></div>';
    cleanupConcierge(container);
    root.innerHTML = '<div class="jsi-success"><p class="jsi-success-kicker">Submission status</p><h3>Your submission has been sent.</h3><p>Thank you. Your intake has been received.</p><p>Your submission has been recognized and prepared for JapanSolved™ review. Our team will match your payment route and intake details, assess your request, and contact you directly by email with the next steps as soon as we can.</p><p>Please watch your email for our reply. If you do not receive anything within 3 days, check your spam or promotions folder first. You may also send us a follow-up message through the <a class="jsi-inline-followup-link" href="'+FOLLOWUP_URL+'">Follow-Up on Existing Request</a> link in the footer below.</p><p>Thank you for trusting JapanSolved™. We look forward to serving your request with care, structure, and practical solutions.</p>'+details+'<div class="jsi-followup"><p>If you need to add something more, correct something, or mention something you forgot, please send us a follow-up here:</p><a class="jsi-btn jsi-btn-main" href="'+FOLLOWUP_URL+'">Add or correct details</a></div></div>';
    syncIntakeTheme(container);
    hardVisibleRepair(root);
    scrollToStepTop(container,'auto');
  }


  function setIntakeFrameMode(container, mode){
    try {
      const form = container && (container.tagName === 'FORM' ? container : qs(container,'form') || qs(container,'.jsi-card') || qs(container,'.jsi-form-transport'));
      if(form && form.classList){
        form.classList.remove(
          'jsi-card--step','jsi-card--review','jsi-card--success',
          'jsi-form-transport--step','jsi-form-transport--review','jsi-form-transport--success'
        );
        form.classList.add('jsi-form-transport');
        form.classList.add('jsi-card--' + mode);
        form.classList.add('jsi-form-transport--' + mode);
      }
      const shell = container && qs(container,'.jsi-shell');
      if(shell && shell.classList){
        shell.classList.remove('jsi-shell--step','jsi-shell--review','jsi-shell--success');
        shell.classList.add('jsi-shell--' + mode);
      }
      const root = container && qs(container,'[data-jsi-root]');
      if(root && root.classList){
        root.classList.remove('jsi-root--step','jsi-root--review','jsi-root--success');
        root.classList.add('jsi-root--' + mode);
      }
      if(container && container.classList){
        container.classList.remove('jsi-mode-step','jsi-mode-review','jsi-mode-success');
        container.classList.add('jsi-mode-' + mode);
      }
    } catch(e){}
  }

  function colorLuminance(rgb){
    const m = String(rgb || '').match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
    if(!m) return 255;
    return (0.2126*Number(m[1])) + (0.7152*Number(m[2])) + (0.0722*Number(m[3]));
  }

  function explicitThemeSignal(){
    const html = document.documentElement;
    const body = document.body || document.documentElement;
    const exactAttrs = [
      html.getAttribute('data-js-theme'), html.getAttribute('data-theme'), html.getAttribute('data-color-scheme'), html.getAttribute('data-mode'),
      body.getAttribute && body.getAttribute('data-js-theme'), body.getAttribute && body.getAttribute('data-theme'), body.getAttribute && body.getAttribute('data-color-scheme'), body.getAttribute && body.getAttribute('data-mode')
    ].map(v => String(v || '').trim().toLowerCase()).filter(Boolean);
    for(const v of exactAttrs){
      if(v === 'dark' || v === 'theme-dark' || v === 'color-scheme-dark') return 'dark';
      if(v === 'light' || v === 'theme-light' || v === 'color-scheme-light') return 'light';
    }
    const classText = [html.className, body.className].map(v => String(v || '').toLowerCase()).join(' ');
    if(/(^|\s)(js-theme-light|theme-light|color-scheme-light|light-mode)(\s|$)/.test(classText)) return 'light';
    if(/(^|\s)(js-theme-dark|theme-dark|color-scheme-dark|dark-mode)(\s|$)/.test(classText)) return 'dark';
    const lp = document.querySelector('.jslp-body');
    if(lp){
      const lpClasses = String(lp.className || '').toLowerCase();
      if(/(^|\s)jslp-light-view(\s|$)/.test(lpClasses)) return 'light';
      if(/(^|\s)jslp-dark-view(\s|$)/.test(lpClasses)) return 'dark';
    }
    return '';
  }

  function detectDarkView(){
    const explicit = explicitThemeSignal();
    if(explicit === 'dark') return true;
    if(explicit === 'light') return false;
    return false;
  }

  function syncIntakeTheme(container){
    if(!container) return;
    const dark = detectDarkView();
    const theme = dark ? 'dark' : 'light';
    if(container.getAttribute('data-jsi-theme') === theme && container.classList.contains('jsi-theme-stable')) return;
    container.setAttribute('data-jsi-theme', theme);
    container.classList.add('jsi-theme-stable');
    container.classList.toggle('jsi-dark-view', dark);
    container.classList.toggle('jsi-light-view', !dark);
  }

  function scheduleIntakeThemeSync(container){
    if(!container) return;
    if(container.__jsiiThemeRAF) window.cancelAnimationFrame(container.__jsiiThemeRAF);
    container.__jsiiThemeRAF = window.requestAnimationFrame(function(){
      container.__jsiiThemeRAF = 0;
      syncIntakeTheme(container);
    });
  }

  function renderApp(container){
    const handle = container.getAttribute('data-js-intake-page') || urlParam('intake') || '';
    const record = findRecord(handle);
    if(!record) return;
    try { container.classList.add('jsi-doctrine-hard-override'); } catch(e){}
    syncIntakeTheme(container);
    try {
      const themeObserver = new MutationObserver(() => scheduleIntakeThemeSync(container));
      themeObserver.observe(document.documentElement, {attributes:true, attributeFilter:['class','data-js-theme','data-theme','data-color-scheme','data-mode']});
      if(document.body) themeObserver.observe(document.body, {attributes:true, attributeFilter:['class','data-js-theme','data-theme','data-color-scheme','data-mode']});
      window.addEventListener('japansolved-theme-change', () => scheduleIntakeThemeSync(container), {passive:true});
      window.addEventListener('japansolved-view-change', () => scheduleIntakeThemeSync(container), {passive:true});
    } catch(e){}
    const form = container.tagName === 'FORM' ? container : qs(container,'form') || container;
    const root = qs(container,'[data-jsi-root]') || container;
    const posted = qs(container,'[data-jsi-posted-success], .jsi-posted-success');
    const successParam = /(?:^|[?&])contact_posted=true(?:&|$)/i.test(window.location.search || '') || /(?:^|[?&])customer_posted=true(?:&|$)/i.test(window.location.search || '');
    if(posted || successParam){
      const stored = readReceipt(record);
      if(posted) posted.hidden = true;
      if(stored && stored.payload){ renderSuccess(container, root, record, buildCaseFileModel(record, stored.payload)); }
      else { renderSuccess(container, root, record, null); }
      return;
    }
    const draft = readDraft(record);
    const state = { step: Math.min(Number(draft.step || 0), Math.max((record.steps || []).length-1,0)), answers: draft.answers || {}, mode:'form' };
    normalizeAnswerAliases(record, state.answers);
    if(form && !form.__jsiiNativeSubmitGuard){
      form.__jsiiNativeSubmitGuard = true;
      form.addEventListener('submit', function(ev){
        if(form.getAttribute('data-jsi-submit-prepared') === 'true') return;
        ev.preventDefault();
        showSubmissionProblem(qs(form,'[data-jsi-root]') || form, 'Please use the visible Send Intake Details button from the review screen so the secure intake packet can be prepared before the contact-form handoff.');
      }, true);
    }
    if(urlParam('checkout_email')) state.answers.checkout_email = urlParam('checkout_email');
    if(urlParam('order_reference')) state.answers.order_reference = urlParam('order_reference');
    if(urlParam('product')) state.answers.selected_product_path = urlParam('product');

    function draw(){
      setIntakeFrameMode(container, 'step');
      cleanupConcierge(container);
      root.innerHTML = '';
      root.appendChild(create('p','jsi-kicker jsi-native-eyebrow','<span class="jsi-kicker-line">J A P A N S O L V E D™</span><span class="jsi-kicker-line">N A T I V E&nbsp;&nbsp;&nbsp;&nbsp;I N T A K E</span>'));
      root.appendChild(create('h2','jsi-title jsi-shine-heading',esc(visibleIntakeTitle(record))));
      const descParagraphs = Array.isArray(record.descriptor_paragraphs) ? record.descriptor_paragraphs.filter(Boolean) : null;
      if(descParagraphs && descParagraphs.length){
        root.appendChild(create('p','jsi-desc jsi-desc-paragraphs',descParagraphs.map(function(txt, idx){
          return '<span class="jsi-desc-para jsi-desc-para-'+(idx+1)+'">'+esc(txt)+'</span>';
        }).join('')));
      } else {
        const desc1 = record.descriptor_line1 || ('A guided, paginated intake for '+(record.page_title || 'this JapanSolved™ desk')+'.');
        const desc2 = record.descriptor_line2 || 'Payment first. Case file second. Boundaries protected.';
        root.appendChild(create('p','jsi-desc','<span class="jsi-desc-line1">'+esc(desc1)+'</span><span class="jsi-desc-line2">'+esc(desc2)+'</span>'));
      }
      if(record._fallback_reason){ root.appendChild(create('p','jsi-fallback-note','Fallback intake loaded: '+esc(record._fallback_reason)+'. This preserves routing until the specialist form is assigned.')); }
      const progress = create('div','jsi-progress','<span></span>');
      qs(progress,'span').style.width = Math.max(8, ((state.step+1)/(record.steps || []).length)*100)+'%';
      const progressWrap = create('div','jsi-progress-wrap');
      progressWrap.appendChild(progress);
      progressWrap.appendChild(renderStepRail(record,state));
      root.appendChild(progressWrap);
      const step = record.steps[state.step];
      const card = create('section','jsi-step');
      card.appendChild(create('p','jsi-step-count','Step '+(state.step+1)+' of '+record.steps.length));
      card.appendChild(create('h3','jsi-step-title jsi-shine-heading',esc(step.title)));
      if(step.tone) card.appendChild(create('p','jsi-step-tone',esc(step.tone)));
      (step.fields||[]).forEach(f=> card.appendChild(renderField(f, record, state)));
      if(step.id === 'payment_gate') card.appendChild(renderPaymentRoutes(record));
      if(state.step === record.steps.length-1) card.appendChild(renderCaptchaNotice('step'));
      const nav = create('div','jsi-nav');
      const back = create('button','jsi-btn jsi-btn-ghost','Back'); back.type='button'; back.disabled = state.step===0;
      const save = create('button','jsi-btn jsi-btn-soft','Save draft in this browser'); save.type='button';
      const clearStep = create('button','jsi-btn jsi-btn-danger-soft','Clear details / start again'); clearStep.type='button';
      const next = create('button','jsi-btn jsi-btn-main',state.step === record.steps.length-1 ? 'Review your details' : 'Continue'); next.type='button';
      nav.appendChild(back); nav.appendChild(save); nav.appendChild(clearStep); nav.appendChild(next); card.appendChild(nav); root.appendChild(card);
      syncIntakeTheme(container);
      hardVisibleRepair(root);
      setupConciergeUX(container, card);
      refreshConditionalFields(card, record, state);
      qsa(card,'input, textarea, select').forEach(el=>{
        el.addEventListener('change',()=>{ Object.assign(state.answers, collectVisibleAnswers(card)); normalizeAnswerAliases(record,state.answers); refreshConditionalFields(card, record, state); saveDraft(record,state); refreshFieldCompletion(card); });
        el.addEventListener('input',()=>{ Object.assign(state.answers, collectVisibleAnswers(card)); normalizeAnswerAliases(record,state.answers); refreshConditionalFields(card, record, state); saveDraft(record,state); refreshFieldCompletion(card); });
      });
      back.addEventListener('click',()=>{ blurActive(); Object.assign(state.answers, collectVisibleAnswers(card)); state.step=Math.max(0,state.step-1); saveDraft(record,state); draw(); scrollToStepTop(container,'smooth'); });
      save.addEventListener('click',()=>{ Object.assign(state.answers, collectVisibleAnswers(card)); saveDraft(record,state); save.textContent='Draft saved'; setTimeout(()=>save.textContent='Save draft in this browser',1300); });
      clearStep.addEventListener('click',()=>{
        const ok = window.confirm ? window.confirm('Clear all saved intake details and start again?') : true;
        if(!ok) return;
        state.step = 0;
        state.answers = {};
        clearDraft(record);
        clearReceipt(record);
        draw();
        scrollToStepTop(container,'smooth');
      });
      next.addEventListener('click',()=>{
        blurActive();
        Object.assign(state.answers, collectVisibleAnswers(card));
        if(!validateStep(card, record, state, container)) return;
        if(step.id === 'payment_gate' && !isPaid(state) && record.unpaid_behavior === 'show_product_routes_and_stop'){
          const existing = qs(card,'.jsi-error-block'); if(existing) existing.remove();
          const err=create('p','jsi-error-block','This desk requires a matched payment path before the detailed intake opens. Use the correct product route above, then return with your checkout email and order reference.'); card.insertBefore(err, nav); scrollToTarget(err,'smooth'); return;
        }
        if(state.step < record.steps.length-1){ state.step++; saveDraft(record,state); draw(); scrollToStepTop(container,'smooth'); }
        else { renderReview(container, form, record, state); }
      });
    }

    function renderReview(container, form, record, state){
      normalizeAnswerAliases(record, state.answers);
      const invalid = firstInvalidRecordStep(record, state);
      if(invalid){
        state.step = invalid.step;
        saveDraft(record,state);
        draw();
        setTimeout(()=>{ const card = qs(root,'.jsi-step'); if(card) validateStep(card, record, state, container); }, 90);
        return;
      }
      setIntakeFrameMode(container, 'review');
      const payload = compilePayload(record,state);
      const model = buildCaseFileModel(record,payload);
      cleanupConcierge(container);
      root.innerHTML = renderReviewHtml(model);
      syncIntakeTheme(container);
      hardVisibleRepair(root);
      setupTextareaAutoGrow(container, root);
      root.appendChild(renderCaptchaNotice('review'));
      const nav = create('div','jsi-nav jsi-review-nav');
      const edit = create('button','jsi-btn jsi-btn-ghost','Go back and edit details'); edit.type='button';
      const clear = create('button','jsi-btn jsi-btn-danger-soft','Clear details / start again'); clear.type='button';
      const submit = create('button','jsi-btn jsi-btn-main','Send intake details'); submit.type='submit';
      submit.setAttribute('data-jsi-visible-submit','true');
      nav.appendChild(edit); nav.appendChild(clear); nav.appendChild(submit); root.appendChild(nav);
      edit.addEventListener('click',()=>{ state.step = Math.max(0, record.steps.length-1); draw(); scrollToStepTop(container,'smooth'); });
      clear.addEventListener('click',()=>{
        const ok = window.confirm ? window.confirm('Clear all saved intake details and start again?') : true;
        if(!ok) return;
        state.step = 0;
        state.answers = {};
        clearDraft(record);
        clearReceipt(record);
        if(form){ form.removeAttribute('data-jsi-submit-prepared'); form.classList.remove('is-submitting'); }
        draw();
        scrollToStepTop(container,'smooth');
      });
      submit.addEventListener('click',(ev)=>{
        const prepared = prepareSubmissionFields(container, form, record, state, root);
        if(!prepared){
          ev.preventDefault();
          ev.stopPropagation();
        }
      }, {capture:true});
      scrollToStepTop(container,'smooth');
    }

    function submitThroughNativeButton(form){
      if(!form) return;
      let realBtn = form.querySelector('[data-jsi-real-submit]') || form.querySelector('button[type="submit"], input[type="submit"]');
      if(!realBtn){
        realBtn = document.createElement('button');
        realBtn.type = 'submit';
        realBtn.className = 'jsi-native-submit';
        realBtn.textContent = 'Submit intake';
        realBtn.setAttribute('aria-hidden','true');
        realBtn.tabIndex = -1;
        form.appendChild(realBtn);
      }
      realBtn.setAttribute('data-jsi-real-submit','true');
      if(realBtn.disabled) realBtn.disabled = false;
      const now = Date.now();
      const last = Number(form.getAttribute('data-jsi-last-submit-at') || 0);
      if(form.__jsiiSubmitting || (last && now - last < SUBMIT_LOCK_MS)) return;
      form.__jsiiSubmitting = true;
      form.setAttribute('data-jsi-last-submit-at', String(now));
      form.setAttribute('data-jsi-submit-prepared','true');
      form.classList.add('is-submitting');
      qsa(form,'.jsi-btn, button, input[type="submit"]').forEach(btn=>{
        if(btn === realBtn || btn.hasAttribute('data-jsi-real-submit')) return;
        if(!btn.disabled) btn.setAttribute('data-jsi-temp-disabled','true');
        btn.setAttribute('disabled','disabled');
      });
      setTimeout(()=>{
        form.__jsiiSubmitting = false;
        form.classList.remove('is-submitting');
        qsa(form,'[data-jsi-temp-disabled]').forEach(btn=>{ btn.removeAttribute('disabled'); btn.removeAttribute('data-jsi-temp-disabled'); });
      }, SUBMIT_LOCK_MS);
      try{
        if(form.requestSubmit) form.requestSubmit(realBtn);
        else realBtn.click();
      }catch(e){
        realBtn.click();
      }
    }

    function prepareSubmissionFields(container, form, record, state, root){
      if(!form) return false;
      if(form.__jsiiSubmitting) return false;
      normalizeAnswerAliases(record, state.answers);
      const invalid = firstInvalidRecordStep(record, state);
      if(invalid){
        showSubmissionProblem(root || qs(form,'[data-jsi-root]') || form, 'Please go back and complete “'+getFieldLabel(record, invalid.field.id)+'” before sending. The intake cannot be submitted until every required step is complete.');
        return false;
      }
      const payload = compilePayload(record,state);
      const model = buildCaseFileModel(record,payload);
      const hiddenBody = qs(form,'[data-jsi-contact-body]') || qs(form,'textarea[name="contact[body]"]');
      const hiddenEmail = qs(form,'[data-jsi-contact-email]') || qs(form,'input[name="contact[email]"]');
      const hiddenName = qs(form,'[data-jsi-contact-name]') || qs(form,'input[name="contact[name]"]');

      const clientName = state.answers.client_name || payload.client_name || 'JapanSolved Intake Client';
      const clientEmail = String(state.answers.contact_email || payload.contact_email || state.answers.checkout_email || payload.checkout_email || '').trim();
      const paymentEmail = String(state.answers.checkout_email || payload.checkout_email || '').trim();

      if(!isValidEmail(clientEmail)){
        showSubmissionProblem(root || qs(form,'[data-jsi-root]') || form, 'Please enter a valid best contact email before sending. Use one @ symbol and a real domain, such as name@example.com.');
        return false;
      }
      if(paymentEmail && !isValidEmail(paymentEmail)){
        showSubmissionProblem(root || qs(form,'[data-jsi-root]') || form, 'Please correct the payment email before sending. Use one @ symbol and a real domain, such as name@example.com.');
        return false;
      }

      disableShopifyExtraContactFields(form);
      populateShopifyStructuredFields(form, record, model, payload);
      if(hiddenBody) hiddenBody.value = minimalContactBody(record,payload);
      if(hiddenEmail) hiddenEmail.value = clientEmail;
      if(hiddenName) hiddenName.value = clientName;

      if(hiddenEmail && hiddenEmail.type === 'email' && !hiddenEmail.checkValidity()){
        showSubmissionProblem(root || qs(form,'[data-jsi-root]') || form, 'The contact email did not pass the browser email check. Please go back and correct it before sending.');
        return false;
      }

      saveReceipt(record,{payload:payload, saved_at:new Date().toISOString()});
      clearDraft(record);
      form.__jsiiSubmitting = true;
      form.setAttribute('data-jsi-submit-prepared','true');
      form.classList.add('is-submitting');
      if(container) container.classList.add('jsi-submission-lock');
      startSubmitWatchdog(container, form, root || qs(form,'[data-jsi-root]') || form);
      return true;
    }

    function prepareSubmission(container, form, record, state){
      const rootNode = qs(form,'[data-jsi-root]') || form;
      if(prepareSubmissionFields(container, form, record, state, rootNode)){
        submitThroughNativeButton(form);
      }
    }
    draw();
  }


  function normalizeVisibleText(root){
    const oldCheckoutA = ['Checkout',' email address'].join('');
    const oldCheckoutB = ['Checkout',' email, if already paid'].join('');
    const oldCheckoutC = ['Payment Checkout',' email address'].join('');
    const oldCheckoutD = ['Email used',' at payment checkout'].join('');
    const oldSubmit = ['Submit native',' intake'].join('');
    const oldReview = ['Review final',' page'].join('');
    const replacements = [
      [new RegExp('^'+oldCheckoutA+'\\s*\\*?$','i'), 'Payment email address'],
      [new RegExp('^'+oldCheckoutB+'\\s*\\*?$','i'), 'Payment email address'],
      [new RegExp('^'+oldCheckoutC+'\\s*\\*?$','i'), 'Payment email address'],
      [new RegExp('^'+oldCheckoutD+'\\s*\\*?$','i'), 'Payment email address'],
      [new RegExp(oldSubmit,'i'), 'Send intake details'],
      [new RegExp(oldReview,'i'), 'Review your details']
    ];
    qsa(root,'.jsi-label, label, button, .jsi-btn, h3, h4, p, span').forEach(el=>{
      const hasChildElements = el.children && el.children.length > 0;
      if(hasChildElements && !el.classList.contains('jsi-label')) return;
      let t = String(el.textContent || '').trim();
      replacements.forEach(pair=>{
        if(pair[0].test(t)){
          if(el.classList.contains('jsi-label') && /\*$/.test(t)) el.innerHTML = esc(pair[1]) + ' <span class="jsi-req">*</span>';
          else el.textContent = pair[1];
        }
      });
    });
  }

  function hardenOptionControls(root){
    qsa(root,'.jsi-option').forEach(row=>{
      const input = qs(row,'input[type="checkbox"], input[type="radio"]');
      if(!input) return;
      row.classList.add(input.type === 'radio' ? 'jsi-option-radio' : 'jsi-option-checkbox');
      let control = qs(row,'.jsi-control');
      if(!control){
        control = create('span','jsi-control','');
        input.insertAdjacentElement('afterend', control);
      }
      let text = qs(row,'.jsi-option-text');
      if(!text){
        const pieces=[];
        Array.from(row.childNodes).forEach(node=>{
          if(node === input || node === control) return;
          if(node.nodeType === 3 && node.textContent.trim()) pieces.push(node.textContent.trim());
          if(node.nodeType === 1 && !node.classList.contains('jsi-control')) pieces.push(node.textContent.trim());
        });
        Array.from(row.childNodes).forEach(node=>{
          if(node !== input && node !== control) row.removeChild(node);
        });
        text = create('span','jsi-option-text',esc(pieces.join(' ').trim()));
        row.appendChild(text);
      }
    });
  }


  function quarantineSelects(root){
    qsa(root,'select.jsi-input').forEach(sel=>{
      if(sel.parentElement && sel.parentElement.classList && sel.parentElement.classList.contains('jsi-select-wrap')){
        sel.parentElement.classList.add('jsi-select-wrap');
      } else if(sel.parentNode){
        const wrap = create('span','jsi-select-wrap');
        sel.parentNode.insertBefore(wrap, sel);
        wrap.appendChild(sel);
      }
      try {
        sel.style.backgroundImage = 'none';
        sel.style.backgroundRepeat = 'no-repeat';
        sel.style.backgroundSize = 'auto';
        sel.style.backgroundPosition = 'initial';
        sel.style.webkitAppearance = 'none';
        sel.style.appearance = 'none';
      } catch(e){}
      Array.from(sel.options || []).forEach(opt=>{ opt.text = cleanRouteLabel(opt.text); });
    });
  }

  function hardenPaymentDock(root){
    qsa(root,'.jsi-payment-routes').forEach(panel=>{
      const list = qs(panel,'.jsi-payment-route-list, ul');
      const routeCount = list ? qsa(list,'li').length : qsa(panel,'a').length;
      const shell = panel.closest('[data-js-intake-page]');
      const handle = String(panel.getAttribute('data-jsi-payment-handle') || (shell && shell.getAttribute('data-js-intake-page')) || '').trim();
      if(handle) panel.setAttribute('data-jsi-payment-handle', handle);
      if(routeCount) panel.setAttribute('data-jsi-payment-count', String(routeCount));
      if(routeCount) panel.classList.add('jsi-payment-count-' + routeCount);
      if(routeCount >= 4) panel.classList.add('jsi-payment-grid-eligible');
      if(handle === 'japan-private-travel-companion-sabbatical-reset'){
        panel.classList.add('jsi-payment-single-column-lock','jsi-payment-private-companion-lock');
      }
      let heading = qs(panel,'.jsi-payment-heading') || qs(panel,'h4');
      if(!heading){
        heading = create('h4','jsi-payment-heading jsi-shine-heading','Complete payment before continuing');
        panel.insertBefore(heading, panel.firstChild);
      }
      heading.classList.add('jsi-payment-heading','jsi-shine-heading');
      if(!String(heading.textContent || '').trim()) heading.textContent = 'Complete payment before continuing';
      qsa(panel,'.jsi-payment-subtext, .jsi-private-route-note, .jsi-payment-helper-note, .jsi-fineprint').forEach(el=>{
        el.classList.add('jsi-payment-copy-rail');
      });
      qsa(panel,'.jsi-private-route-note').forEach(el=>{
        if(!qs(el,'strong') && /^Already approved privately\?/i.test(String(el.textContent || '').trim())){
          el.innerHTML = String(el.textContent || '').replace(/^Already approved privately\?/i,'<strong>Already approved privately?</strong>');
        }
      });
      qsa(panel,'.jsi-payment-route-list, ul').forEach(list=>list.classList.add('jsi-payment-route-list'));
      qsa(panel,'a').forEach(a=>{ a.classList.add('jsi-payment-route-button'); a.textContent = cleanRouteLabel(a.textContent); });
    });
  }



  function hardenEyebrow(root){
    qsa(root,'.jsi-kicker, .jsi-native-eyebrow').forEach(el=>{
      el.classList.add('jsi-kicker','jsi-native-eyebrow');
      const txt = String(el.textContent || '').replace(/\s+/g,' ').trim().toUpperCase();
      const hasLineMarkup = qs(el,'.jsi-kicker-line') || qs(el,'.jsi-eyebrow-line');
      if(!hasLineMarkup && (txt.indexOf('NATIVE') >= 0 || txt.indexOf('INTAKE') >= 0 || txt.indexOf('JAPANSOLVED') >= 0)){
        el.innerHTML = '<span class="jsi-kicker-line">J A P A N S O L V E D™</span><span class="jsi-kicker-line">N A T I V E&nbsp;&nbsp;&nbsp;&nbsp;I N T A K E</span>';
      }
      qsa(el,'.jsi-eyebrow-line').forEach(span=>span.classList.add('jsi-kicker-line'));
    });
  }

  function hardenPaymentCopyRail(root){
    qsa(root,'.jsi-payment-routes').forEach(panel=>{
      const selectors = ['.jsi-payment-subtext','.jsi-private-route-note','.jsi-payment-helper-note','.jsi-fineprint','p'];
      selectors.forEach(sel=>qsa(panel,sel).forEach(el=>{
        if(el.closest('li')) return;
        el.classList.add('jsi-payment-copy-rail');
        if(el.classList.contains('jsi-private-route-note')) el.classList.add('jsi-payment-private-copy');
      }));
    });
  }

  function hardVisibleRepair(root){
    normalizeVisibleText(root || document);
    hardenEyebrow(root || document);
    hardenPaymentCopyRail(root || document);
    hardenOptionControls(root || document);
    quarantineSelects(root || document);
    hardenPaymentDock(root || document);
  }

  function init(){ qsa(document,'[data-js-intake]').forEach(function(container){ container.setAttribute('data-js-intake-version', ENGINE_VERSION); renderApp(container); }); }
  window[NS] = { version:ENGINE_VERSION, init, getRegistry, findRecord, compilePayload, payloadToEmail, buildCaseFileModel, renderReviewHtml, clientReceiptPlainText };
  try { console.info('JapanSolved Native Intake Original NIF Backend Reconciliation v1 loaded'); } catch(e){}
  if(document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
